<!-- app-subdomain-creators\src\Components\CreatorChatMainComponent -->
<!-- Creator app side component -->

<template>
  <div
    @dragenter="handleDragEnter"
    @dragleave="handleDragLeave"
    @dragover.prevent
    @drop.prevent="handleDrop"
    class="flex flex-col relative h-full"
  >
         <CreatorChatVoiceMemoRecorder
          :show="showVoiceMemoModal"
          :feature-instance-id="props.featureInstanceId"
          :member-id="props.member ? props.member.id : null"
          @close="closeVoiceMemoOverlay"
          @recorded="handleVoiceMemoRecorded"
        />



<!-- Image / PDF / TEXT overlay viewer -->
<transition name="fade" mode="out-in">
  <div
    v-if="isImageOverlayOpen"
    class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-10"
    @click="isImageOverlayOpen = false"
  >
    <!-- Modal window -->
    <div
      class="p-6 rounded-lg shadow-2xl w-full h-full bg-black/40 backdrop-blur-sm relative flex items-center justify-center overflow-hidden"
      @click.stop
    >

    <!-- Right Side Tools -->
    <div class="absolute top-4 right-4 flex items-center gap-3">

      <!-- Close button -->
             <ArrowDownTrayIcon
              v-if="currentImageSrc"
              :href="currentImageSrc"
              download
              class="w-11 font-semibold text-zinc-200 bg-zinc-700 hover:bg-black/60 hover:text-[#2D8CFF] px-2 py-1 rounded-md"
            >
            </ArrowDownTrayIcon>


      <!-- Close button -->
      <button
        @click="isImageOverlayOpen = false"
        class="px-2 py-1 rounded-md text-3xl font-bold leading-none bg-zinc-700 hover:bg-black hover:bg-opacity-65 text-zinc-200 z-10"
      >
        &times;
      </button>

      </div>

      <!-- Optional file name -->
      <div
        v-if="currentFileName"
        class="absolute top-4 left-4 max-w-[60%] text-sm md:text-base font-semibold text-zinc-100 bg-black/40 px-3 py-1 rounded-lg truncate"
      >
        {{ currentFileName }}
      </div>

      <!-- Zoom Slider (IMAGES ONLY) -->
      <div
        v-if="overlayType === 'image'"
        class="absolute bottom-4 left-4 flex items-center z-10"
      >
        <span class="text-zinc-200 text-xl mr-2 cursor-pointer">-</span>
        <input
          type="range"
          v-model="zoomLevel"
          min="1"
          max="3"
          step="0.1"
          class="w-32"
        />
        <span class="text-zinc-200 text-xl cursor-pointer ml-2">+</span>
      </div>

      <!-- IMAGE viewer -->
      <img
        v-if="overlayType === 'image'"
        :src="currentImageSrc"
        alt="Enlarged View"
        class="max-w-[80%] max-h-[70%] object-contain rounded-lg transition-transform duration-300"
        :style="`transform: scale(${zoomLevel})`"
      />

      <!-- PDF viewer -->
      <div
        v-else-if="overlayType === 'pdf'"
        class="w-full h-full flex items-center justify-center"
      >
        <iframe
          :src="currentImageSrc"
          class="w-full h-full rounded-lg bg-white"
        ></iframe>
      </div>

      <!-- TEXT viewer -->
      <div
        v-else-if="overlayType === 'text'"
        class="w-full h-full flex items-center justify-center mx-3"
      >
        <div
          class="w-full max-w-6xl h-[80vh] bg-white/95 rounded-lg shadow-xl p-4 md:p-6 overflow-y-auto scrollbar scrollbar-thumb-zinc-400 scrollbar-track-zinc-200 dark:scrollbar-track-zinc-500 scrollbar-thumb-rounded-full scrollbar-track-rounded-full scrollbar-hover:scrollbar-track-zinc-300 border border-zinc-200"
        >
          <!-- header: filename + optional download -->
          <div class="flex items-center justify-between mb-3 gap-3">
            <div
              class="text-xs md:text-sm font-semibold text-zinc-700 truncate"
            >
              {{ currentFileName || 'Text file' }}
            </div>
            
          </div>

          <!-- loading / error / content -->
          <div v-if="overlayLoading" class="text-xs text-zinc-500">
            Loading…
          </div>
          <div v-else-if="overlayError" class="text-xs text-red-500">
            {{ overlayError }}
          </div>
          <pre
            v-else
            class="text-xs md:text-sm font-mono text-zinc-800 whitespace-pre-wrap break-words"
          >
{{ overlayTextContent }}
          </pre>
        </div>
      </div>

            <!-- GENERIC FILE viewer (Word / Excel / etc) -->
      <div
        v-else-if="overlayType === 'file'"
        class="w-full h-full flex items-center justify-center mx-3"
      >
        <div
          class="w-full max-w-md bg-white/95 rounded-lg shadow-xl p-6 flex flex-col items-center gap-4 border border-zinc-200"
        >
          <div class="flex items-center justify-center">
            <img
              v-if="overlayFileKind === 'word'"
              class="w-20"
              src="@/assets/media/images/chat-word-doc-icon.svg"
              alt="Word document"
            />
            <img
              v-else-if="overlayFileKind === 'excel'"
              class="w-20"
              src="@/assets/media/images/chat-excel-doc-icon.svg"
              alt="Excel document"
            />
            <img
              v-else-if="overlayFileKind === 'csv'"
              class="w-20"
              src="@/assets/media/images/chat-csv-doc-icon.svg"
              alt="CSV file"
            />
            <img
              v-else-if="overlayFileKind === 'zip'"
              class="w-20"
              src="@/assets/media/images/chat-zip-doc-icon.svg"
              alt="ZIP archive"
            />
            <img
              v-else
              class="w-20"
              src="@/assets/media/images/chat-word-doc-icon.svg"
              alt="File"
            />
          </div>

          <div class="text-center">
            <div class="text-sm font-semibold text-zinc-800 truncate max-w-xs">
              {{ currentFileName || 'File' }}
            </div>
            <p class="mt-1 text-xs text-zinc-500">
              Download and open this file with your default application.
            </p>
          </div>

          <div class="flex gap-3 mt-3">
            <a
              :href="currentImageSrc"
              download
              class="px-3 py-1 rounded-md text-xs font-semibold bg-[#2D8CFF] text-white hover:bg-[#1B6CD9]"
            >
              Download
            </a>
            <a
              :href="currentImageSrc"
              target="_blank"
              rel="noopener noreferrer"
              class="px-3 py-1 rounded-md text-xs font-semibold bg-zinc-200 text-zinc-800 hover:bg-zinc-300"
            >
              Open in new tab
            </a>
          </div>
        </div>
      </div>


    </div>
  </div>
</transition>


  <!-- video recording overlay for recording video messages in an enlarged view -->
  <CreatorChatVideoMemoRecorder
    :show="showVideoModal"
    :upload-chat-video-fn="uploadChatVideoViaMedia"
    :avatar-url="creatorAvatarUrl"
    @close="showVideoModal = false"
    @send="handleVideoMemoSend"
  />


<!-- Search Overlay -->
<CreatorChatSearchOverlay
  :show-search="props.showSearch"
  :search-images="props.searchImages"
  :search-links="props.searchLinks"
  :search-files="props.searchFiles"
  :feature-instance-id="props.featureInstanceId"
  :member-id="props.member ? props.member.id : null"
  :creator-avatar-src="creatorAvatarSrc"
  :member-avatar-src="memberAvatarSrc"
  :member="props.member"
  @close="emit('toggleSearch')"
  @open-image="({ src, name }) => showImageOverlay(src, 'image', name)"
  @open-pdf="({ src, name }) => showImageOverlay(src, 'pdf', name)"
  @open-text="({ src, name }) => showTextOverlay(src, name)"
  @jump-to-message="handleSearchJump"
/>


<!-- dropzone overlay -->
<div
  v-if="isDraggingOver"
  class="absolute inset-0 bg-black bg-opacity-80 flex items-center justify-center text-white z-30 rounded-lg"
  @dragover.prevent
>
  <div>
    <img
      class="w-40 opacity-80 mx-auto"
      src="@/assets/media/images/cloud.png"
      alt="Dropzone icon"
    />
    <p class="mx-auto font-semibold text-lg">Upload to the chat</p>
  </div>
</div>



<div
  ref="chatContainer"
  class="chat-scroll flex flex-col h-[500px] px-2 md:px-14 py-10 scroll-smooth scroll-p-10 overflow-y-auto flex-grow pb-16 md:pb-0"
  @scroll="onChatScroll"
>

<div class="flex flex-col items-center justify-center pb-4">
  
  <!-- 🔄 Top spinner while loading older pages -->
  <div
    v-if="isLoadingOlder"
    class="flex flex-col items-center mt-4"
  >
    <div class="relative flex items-center justify-center mt-2">
      <!-- outer ring -->
      <div class="absolute inset-0 rounded-full border-4 border-[#6831ff33]"></div>
      <!-- spinning ring -->
      <div class="size-8 animate-spin rounded-full border-4 border-[#6831ff] border-t-transparent"></div>
    </div>
    <span class="mt-2 text-xs text-zinc-400">
      Loading older messages…
    </span>
  </div>

  <!-- 🦕 Beginning-of-chat banner (only when NOT loading older) -->
  <div
    v-else-if="initialLoadDone && !hasMoreAbove && Array.isArray(messages) && messages.length > 0"
    class="flex items-center text-xs text-zinc-500 mt-4 mb-2"
  >
    <img
      src="@/assets/media/images/platforma-dino.png"
      alt="Beginning"
      class="w-20 h-20 mb-2 opacity-80"
    />

    <div class="items-center gap-3 text-center">
      <div class="font-semibold text-zinc-700">
        This is the very beginning
      </div>
      <div class="text-zinc-500 text-center">
        You've made your way through all available messages.
      </div>
    </div>
  </div>
</div>

  <!-- Empty State Message -->
 <!-- Unified height wrapper -->
  <div class="flex-1 flex items-center justify-center">

<!-- 🔹 No member selected -->
    <div
      v-if="!props.member"
      class="text-center text-gray-500 flex flex-col -mt-10"
    >
      <p class="text-2xl mt-2 font-semibold">
        Start A Conversation
      </p>
  
      <p class="text-base mt-2">
        Choose a member from the list on the left to open their chat.
      </p>
      <p class="text-base ">
        All messages, media, and replies will show here.
      </p>

      <div class="flex gap-2 mx-auto text-center items-center mt-4">
      <ChevronDoubleLeftIcon class="w-10 text-[#2D8CFF] -ml-10 animate-pulse"></ChevronDoubleLeftIcon>
      <p class="text-base ">
        Select a member on the left
      </p>
      </div>
      

    </div>

    <!-- 🔹 Member selected -->
    <div v-else class="w-full h-full">

      <!-- 🔹 Loading messages -->
<div
  v-if="isLoadingMessages"
  class="flex items-center justify-center h-full text-gray-500"
>
  <p class="text-sm">Loading messages…</p>
</div>

    <!-- 🔹 No messages yet -->
      <div
  v-else-if="!Array.isArray(messages) || messages.length === 0"
  class="flex items-center justify-center h-full text-gray-500"
>
        <div class="text-center">
          <img
            class="w-[100px] mx-auto"
            src="@/assets/media/images/chat-hand-wave-empty-state.png"
            alt=""
          />
          <p class="text-sm mt-2">
            Be the first to start the conversation with
            {{ props.member.profileName || props.member.name }}.
          </p>
        </div>
      </div>

<!-- Message Loops -->
  <transition-group name="message-fade" tag="div">
    <div
      v-for="(message, index) in messages"
      :key="message.id"
      class="my-3 mx-auto group flex flex-col w-full max-w-[720px] px-2"
    >



    <!-- Date Divider -->
<div
  v-if="shouldShowDateDivider(message, index)"
  class="relative flex items-center justify-center my-8 w-full"
>
  <!-- Full-width line -->
  <div class="absolute inset-x-0 top-1/2 border-t border-zinc-200"></div>

  <!-- Date label -->
  <span
    class="relative z-10 md:text-lg text-sm font-bold text-zinc-700 px-3 bg-white bg-opacity-80 rounded-lg"
  >
    {{ formatDate(message.date) }}
  </span>
</div>

      <!-- Message content (this is your old "Message content loop" inner div) -->
      <div class="flex w-full" :class="isMemberMessage(message) ? 'justify-start' : 'justify-end'">
  <!-- Message content (this is your old "Message content loop" inner div) -->
  <div
    class="relative group w-fit max-w-full md:max-w-[85%]"
    :class="isMemberMessage(message) ? '' : 'ml-0'"
  >

       <!-- Three-dot Menu Button -->
        <div
          v-if="message.senderType === 'creator'"
          :class="{
            'opacity-100': message.showOptions || hoveredMessageId === message.id,
            'opacity-0': !message.showOptions && hoveredMessageId !== message.id
          }"
          class="absolute top-5 -left-16 text-gray-500 cursor-pointer z-20 group-hover:opacity-100 transition-opacity"
          @mouseenter="hoveredMessageId = message.id"
          @mouseleave="handleEllipsisMouseLeave(message)"
          @click.stop="toggleOptions(message)"
        >
          <EllipsisHorizontalIcon class="w-9 text-zinc-400 font-bold" />
        </div>


        <!-- Delete and Edit Options Menu -->
        <div
          v-if="message.showOptions"
          ref="optionsMenu"
          class="absolute -top-20 -left-28 bg-[#ddf0ff] border rounded shadow p-2 text-sm z-30"
          @mouseenter="cancelHideOptions"
          @mouseleave="handleMenuMouseLeave(message)"
        >
          <div
            @click="startEditMessage(message)"
            class="flex gap-2 cursor-pointer hover:bg-zinc-100 rounded-md p-2"
          >
            <PencilIcon class="w-5 h-5 text-black" />
            <p class="cursor-pointer text-black">Edit</p>
          </div>
          <div
            @click="deleteMessage(message)"
            class="flex gap-2 cursor-pointer mt-2 hover:bg-zinc-100 rounded-md p-2"
          >
            <TrashIcon class="w-5 h-5 text-black" />
            <p class="cursor-pointer text-black">Delete</p>
          </div>
          <div
            class="tooltip-arrow absolute left-1/2 -right-1 mt-[10px] w-0 h-0 border-l-[12px] border-transparent border-b-[12px] transform -translate-y-1/2 rotate-45 rounded bg-[#ddf0ff]"
          ></div>
        </div>


        <!-- Avatar -->
        <img
          :src="avatarForMessage(message)"
          :alt="`${message.username}'s avatar`"
          @error="handleMessageAvatarError"
          class="absolute md:top-0 -top-5 w-12 h-12 rounded-full border-4 border-white z-10"
          :class="isMemberMessage(message)
            ? 'left-[-10px] md:left-[-44px]'
            : 'right-[-10px] md:right-[-40px]'"
        />

        <!-- Message Bubble -->
          <div
            :data-message-id="message.id"
            class="rounded-xl p-4 -ml-2 relative"
            :class="[
              isMemberMessage(message) ? 'bg-zinc-100/75' : 'bg-[#00c3ff1c]',
              highlightedMessageId === message.id
                ? 'ring-2 ring-[#2D8CFF] ring-offset-2 ring-offset-white'
                : ''
            ]"
          >

          <div class="justify-between items-center">

            <div
              class="flex gap-2 mb-1 mr-5 md:mr-0 items-center"
              :class="isMemberMessage(message) ? 'justify-start' : 'justify-end'"
            >

              <!-- Active Self-Destruct Icon -->
              <div class="relative" v-if="message.selfDestructEndTime && message.selfDestructEndTime > new Date().getTime()">
                <FireIcon @mouseenter="message.showFireTooltip = true" @mouseleave="message.showFireTooltip = false" class="text-red-600 w-7 h-7 cursor-pointer" />

                <!-- Active Self-Destruct tooltip -->
                <div v-if="message.showFireTooltip" class="absolute text-center w-44 bottom-full p-2 -translate-x-10 mb-3 bg-black opacity-80 text-white text-sm rounded tooltip z-40">
                  This message will self-destruct in
                  {{ secondsToReadableTime(getRemainingSeconds(message.selfDestructEndTime)) }}
                  <div class="tooltip-arrow absolute left-1/2 -right-12 mt-[6px] w-0 h-0 border-l-[12px] border-transparent border-b-[12px] transform -translate-y-1/2 rotate-45 rounded bg-black"></div>
                </div>
              </div>

              <!-- From name -->
              <div class="text-xs font-bold text-gray-500 pl-1">{{ message.username }}</div>
              <!-- Time -->
              <div class="text-xs font-medium text-gray-500">{{ message.time }}</div>
            </div>

            <!-- Message Content (TEXT) -->
<div
  v-if="message.content && message.content.trim().length && message.type !== 'link'"
  class="z-30"
>
  <div v-for="(line, idx) in splitLines(message.content)" :key="idx">
    <span
      v-for="(segment, sIdx) in splitLineIntoSegments(line)"
      :key="sIdx"
      :class="[
        'font-normal',
        'text-zinc-700',
        'whitespace-pre-wrap',
        'break-words',
        fontSizeClass(message.content)
      ]"
    >
      <!-- Plain text part -->
      <template v-if="segment.type === 'text'">
        {{ segment.value }}
      </template>

      <!-- URL part -->
      <template v-else>
        <a
          :href="segment.value"
          class="text-[#2D8CFF] underline break-words font-semibold"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ segment.value }}
        </a>
      </template>
    </span>

    <!-- Preserve explicit line breaks -->
    <br v-if="idx < splitLines(message.content).length - 1" />
  </div>

</div>

<!-- For links -->
<div class="w-full h-full">
  <CreatorChatLinkPreview
    v-if="message.type === 'link'"
    :url="message.content"
  />
</div>

      <!-- 📎 Attachments (multi or single) -->
      <div
  v-if="Array.isArray(message.attachments) && message.attachments.length"
  class="mt-2 flex gap-3"
  :class="message.attachments.some(a => a.type === 'video') ? 'flex-col items-start' : 'flex-wrap'"
>

        <div
          v-for="(att, idx) in message.attachments"
          :key="att.id || att.key || idx"
          :class="[
                    att.type === 'video' || att.type === 'audio' || message.attachments.length === 1
                      ? 'w-full'
                      : 'max-w-[240px]'
                  ]"
        >

          <!-- images -->
          <template v-if="att.type === 'image'">
            <div
              class="cursor-zoom-in inline-block"
              @click="showImageOverlay(att.url || att.data)"
            >
              <img
                :src="att.url || att.data"
                :class="[
                  'rounded-lg shadow-md',
                  message.attachments.length === 1 ? '' : 'max-h-64 object-contain'
                ]"
                alt="Uploaded image"
                @load="handleMessageMediaLoad"
              />
            </div>
            <p
              v-if="att.name"
              class="text-sm font-normal text-zinc-600 mb-1 truncate"
            >
              {{ att.name }}
            </p>
          </template>

        <!-- videos (HLS via chat-videos) -->
        <template v-else-if="att.type === 'video'">
          <CreatorChatInlineVideoPlayer
            :file-id="att.mediaFileId || att.fileId || att.id"
            :poster="buildChatVideoPoster(att)"
            class="rounded-[20px]"
          />
          <p v-if="att.name" class="text-sm font-normal text-zinc-600 mb-1 mt-1 truncate">
            {{ att.name }}
          </p>
        </template>




          <!-- PDF: mini preview + click to open overlay -->
          <template v-else-if="att.type === 'pdf'">
            <div
              class="flex flex-col items-start gap-1 cursor-zoom-in"
              @click="showImageOverlay(att.url || att.data, 'pdf', att.name)"
            >
              <!-- Mini PDF preview -->
              <div
                class="relative w-32 h-44 rounded-md overflow-hidden border border-zinc-300 bg-white"
              >
                      <iframe
                  :src="buildPdfViewerSrc(att.url || att.data)"
                  class="w-full h-full pointer-events-none border-0"
                ></iframe>


                <!-- Small PDF label badge -->
                <div
                  class="absolute top-1 left-1 bg-red-500/70 text-[10px] font-semibold text-white px-1 rounded text-lg"
                >
                  PDF
                </div>
              </div>

              <!-- File name under the preview -->
              <p
                v-if="att.name"
                class="text-[11px] text-zinc-600 max-w-[8rem] truncate"
              >
                {{ att.name }}
              </p>
            </div>
          </template>


            <!-- word docs -->
<template v-else-if="att.type === 'word'">
  <!-- 👇 SINGLE word attachment -->
  <div
    v-if="message.attachments.length === 1"
    class="flex items-center space-x-3 cursor-zoom-in"
    @click="att.url && showFileOverlay(att.url || att.data, att.name, 'word')"
  >
    <div>
      <img
        class="rounded-md w-16"
        src="@/assets/media/images/chat-word-doc-icon.svg"
        alt="Word document icon"
      />
    </div>
    <div>
      <p class="text-sm font-normal text-zinc-600">
        {{ att.name }}
      </p>
      <a
        :href="att.url || att.data"
        download
        class="text-zinc-500 underline cursor-pointer"
        @click.stop
      >
        Download
      </a>
    </div>
  </div>

  <!-- 👇 MULTI word attachment -->
  <div
    v-else
    class="flex flex-col items-start p-3 text-center"
  >
    <div class="flex items-center gap-2 mx-auto">
      <img
        class="w-24 cursor-zoom-in"
        src="@/assets/media/images/chat-word-doc-icon.svg"
        alt="Word document icon"
        @click="att.url && showFileOverlay(att.url || att.data, att.name, 'word')"
      />
    </div>
    <div class="text-center w-40">
      <p class="mt-2 text-xs font-medium text-zinc-700 break-all truncate text-center">
        {{ att.name }}
      </p>
      <a
        :href="att.url || att.data"
        download
        class="text-zinc-500 underline cursor-pointer text-xs"
        @click.stop
      >
        Download
      </a>
    </div>
  </div>
</template>

<!-- excel files -->
<template v-else-if="att.type === 'excel'">
  <!-- 👇 SINGLE excel attachment -->
  <div
    v-if="message.attachments.length === 1"
    class="flex items-center space-x-3 cursor-zoom-in"
    @click="att.url && showFileOverlay(att.url || att.data, att.name, 'excel')"
  >
    <div>
      <img
        class="rounded-md w-16"
        src="@/assets/media/images/chat-excel-doc-icon.svg"
        alt="Excel document icon"
      />
    </div>
    <div>
      <p class="text-sm font-normal text-zinc-600">
        {{ att.name }}
      </p>
      <a
        :href="att.url || att.data"
        download
        class="text-zinc-500 underline cursor-pointer"
        @click.stop
      >
        Download
      </a>
    </div>
  </div>

  <!-- 👇 MULTI excel attachment -->
  <div
    v-else
    class="flex flex-col items-start p-3 text-center"
  >
    <div class="flex items-center gap-2 mx-auto">
      <img
        class="w-24 cursor-zoom-in"
        src="@/assets/media/images/chat-excel-doc-icon.svg"
        alt="Excel document icon"
        @click="att.url && showFileOverlay(att.url || att.data, att.name, 'excel')"
      />
    </div>
    <div class="text-center w-40">
      <p class="mt-2 text-xs font-medium text-zinc-700 break-all truncate text-center">
        {{ att.name }}
      </p>

      <a
        :href="att.url || att.data"
        download
        class="text-zinc-500 underline cursor-pointer text-xs"
        @click.stop
      >
        Download
      </a>
    </div>
  </div>
</template>

<!-- csv files (open in TEXT viewer) -->
<template v-else-if="att.type === 'csv'">
  <!-- 👇 SINGLE csv attachment -->
  <div
    v-if="message.attachments.length === 1"
    class="flex items-center space-x-3 cursor-zoom-in"
    @click="showTextOverlay(att.url || att.data, att.name)"
  >
    <div>
      <img
        class="rounded-md w-16"
        src="@/assets/media/images/chat-csv-doc-icon.svg"
        alt="CSV file icon"
      />
    </div>
    <div>
      <p class="text-sm font-normal text-zinc-600">
        {{ att.name }}
      </p>
      
    </div>
  </div>

  <!-- 👇 MULTI csv attachment -->
  <div
    v-else
    class="flex flex-col items-start p-3 text-center"
  >
    <div class="flex items-center gap-2 mx-auto">
      <img
        class="w-24 cursor-zoom-in"
        src="@/assets/media/images/chat-csv-doc-icon.svg"
        alt="CSV file icon"
        @click="showTextOverlay(att.url || att.data, att.name)"
      />
    </div>
    <div class="text-center w-40">
      <p class="mt-2 text-xs font-medium text-zinc-700 break-all truncate text-center">
        {{ att.name }}
      </p>
    </div>
  </div>
</template>

<!-- zip archives (download only) -->
<template v-else-if="att.type === 'zip'">
  
  <!-- 👇 SINGLE zip attachment -->
  <div class="flex items-center space-x-3"
  v-if="message.attachments.length === 1">
    <div>
      <img
        class="rounded-md w-16"
        src="@/assets/media/images/chat-zip-doc-icon.svg"
        alt="ZIP archive icon"
      />
    </div>
    <div>
      <p class="text-sm font-normal text-zinc-600">
        {{ att.name }}
      </p>
      <a
        :href="att.url || att.data"
        download
        class="text-zinc-500 underline cursor-pointer"
      >
        Download
      </a>
    </div>
  </div>

  <!-- 👇 MULTI zip attachment -->
  <div
    v-else
    class="flex flex-col items-start p-3 text-center"
  >
    <div class="flex items-center gap-2 mx-auto">
      <a
        :href="att.url || att.data"
        download
      >
        <img
          class="w-24 cursor-pointer"
          src="@/assets/media/images/chat-zip-doc-icon.svg"
          alt="ZIP archive icon"
        />
      </a>
    </div>
    <div class="text-center w-40">
      <p class="mt-2 text-xs font-medium text-zinc-700 break-all truncate text-center">
        {{ att.name }}
      </p>
      <a
        :href="att.url || att.data"
        download
        class="text-zinc-500 underline cursor-pointer text-xs"
        @click.stop
      >
        Download
      </a>
    </div>
  </div>
</template>



      <!-- text files -->
      <template v-else-if="att.type === 'text'">
        <!-- 👇 SINGLE text attachment (only one attachment in this message) -->
        <div
          v-if="message.attachments.length === 1"
          class="flex items-center space-x-3"
          @click="showTextOverlay(att.url || att.data, att.name)"
        >
          <div>
            <img
              class="rounded-md w-16 cursor-zoom-in"
              src="@/assets/media/images/chat-txt-doc-icon.svg"
              alt="text document icon"
            />
          </div>
          <div>
            <p class="text-sm font-normal text-zinc-600">
              {{ att.name }}
            </p>
            
          </div>
        </div>

        <!-- 👇 MULTI text attachment (message has > 1 attachment) -->
        <div
          v-else
          class="flex flex-col items-start p-3 text-center"
        >
          <div class="flex items-center gap-2 mx-auto">
            <img
              class="w-24 cursor-zoom-in"
              src="@/assets/media/images/chat-txt-doc-icon.svg"
              alt="text document icon"
              @click="showTextOverlay(att.url || att.data, att.name)"
            />

          </div>
            <div class="text-center w-40">
          <p class="mt-2 text-xs font-medium text-zinc-700 break-all truncate text-center">
            {{ att.name }}
          </p>

        
          </div>
        </div>
      </template>


<!-- audio attachments: voice memos (webm) vs regular mp3/audio -->
<template v-else-if="att.type === 'audio'">
  <!-- Voice memo: audio/webm or “voice memo” name -->
  <CreatorChatVoiceMemoPlayer
    v-if="
      (att.mime && att.mime === 'audio/webm') ||
      (att.name && att.name.toLowerCase().includes('voice memo')) ||
      att.isVoiceMemo
    "
    :src="att.url || att.data"
  />
  <!-- Regular audio / mp3 -->
  <CreatorChatMp3Player
    v-else
    :src="att.url || att.data"
    :name="att.name"
  />
</template>







        </div>
      </div>

        <!-- 🔙 Legacy single-attachment fallback (when attachments[] is empty) -->

        <!-- For IMAGES -->
        <div
          v-else-if="message.type === 'image' && message.file"
          class="mt-2 cursor-zoom-in"
          @click="showImageOverlay(message.file.data)"
        >
          <img
            class="rounded-lg shadow-md"
            :src="message.file.data"
            alt="Uploaded image"
            @load="handleMessageMediaLoad"
          />
          <p
            v-if="message.fileName"
            class="text-sm font-normal text-zinc-600 mb-1 truncate"
          >
            {{ message.fileName }}
          </p>
        </div>

<!-- For VIDEOS (legacy single-file path) -->
<div
  v-else-if="message.type === 'video' && message.file"
  class="rounded-md mt-2"
>
  <div v-if="message.file.loading">
    <!-- ... your loading GIF + progress bar ... -->
  </div>

  <div v-else>
    <!-- HLS path -->
    <CreatorChatInlineVideoPlayer
      v-if="message.file.mediaFileId || message.file.fileId || message.file.id"
      :file-id="message.file.mediaFileId || message.file.fileId || message.file.id"
      :poster="buildChatVideoPoster(att)"
      class="rounded-[20px]"
    />

    <!-- Fallback: old direct MP4 blob -->
    <video
      v-else
      class="rounded-[20px]"
      controls
      @loadedmetadata="handleMessageMediaLoad"
    >
      <source
        :src="message.file.data"
        :type="pendingAttachment?.mime || 'video/mp4'"
      />
      Your browser does not support the video tag.
    </video>

    <p class="text-sm font-normal text-zinc-600 mt-1 mb-1 truncate">
      {{ message.fileName }}
    </p>
  </div>
</div>



        <!-- For PDFs (single file): mini preview + overlay -->
        <div
          v-else-if="message.file && message.file.type === 'pdf'"
          class="mt-2"
        >
          <div
            class="inline-flex flex-col items-start gap-2 cursor-zoom-in"
            @click="showImageOverlay(message.file.data, 'pdf', message.fileName)"
          >
            <!-- Mini PDF preview -->
            <div
              class="relative w-40 h-56 rounded-md overflow-hidden border border-zinc-300 bg-white"
            >
              <iframe
                :src="buildPdfViewerSrc(message.file.data)"
                class="w-full h-full pointer-events-none border-0"
              ></iframe>


              <div
                class="absolute top-1 left-1 bg-red-500 text-[10px] font-semibold text-white px-1 rounded"
              >
                PDF
              </div>
            </div>

            <!-- File name under the preview -->
            <p class="text-xs text-zinc-600 max-w-[10rem] truncate">
              {{ message.fileName || 'PDF document' }}
            </p>
          </div>
        </div>


        <!-- For Word Documents -->
        <div
          v-else-if="message.file && message.file.type === 'word'"
          class="flex items-center space-x-3"
        >
          <div>
            <img
              class="rounded-md w-14"
              src="@/assets/media/images/chat-word-doc-icon.svg"
              alt="text document icon"
            />
          </div>
          <div>
            <p class="text-sm font-normal text-zinc-600">
              {{ message.fileName }}
            </p>
            <a
              :href="message.file.url"
              download
              class="text-zinc-500 underline cursor-pointer"
            >
              Download
            </a>
          </div>
        </div>

        <!-- For Text Files -->
        <div
          v-else-if="message.file && message.file.type === 'text'"
          class="flex items-center space-x-3"
        >
          <div>
            <img
              class="rounded-md w-14"
              src="@/assets/media/images/chat-txt-doc-icon.svg"
              alt="text document icon"
            />
          </div>
          <div>
            <p class="text-sm font-normal text-zinc-600">
              {{ message.fileName }}
            </p>
            
          </div>
        </div>

         <!-- For voice memos -->
        <div v-if="message.type === 'combined' || message.type === 'voiceMemo'">
          <template v-if="message.textContent">
            <div
              v-for="(line, index) in splitLines(message.textContent)"
              :key="index"
            >
              <span
                :class="[
                  'font-normal',
                  'text-zinc-700',
                  'whitespace-normal',
                  'break-words',
                  fontSizeClass.value
                ]"
              >
                {{ line }}<br
                  v-if="index < splitLines(message.textContent).length - 1"
                />
              </span>
            </div>
          </template>

          <div class="flex flex-wrap justify-start items-start gap-4 mt-3">
            <CreatorChatVoiceMemoPlayer
              v-for="(memo, index) in message.voiceMemos"
              :key="memo.id || memo.url || `voiceMemo-${message.id}-${index}`"
              :src="memo.url"
            />
          </div>
        </div>




          </div>

          <!-- 🔥 Self-destruct flames (works for text + link + attachments) -->
        <div
          v-if="message.selfDestructEndTime && message.selfDestructEndTime > new Date().getTime()"
          class="absolute w-full -bottom-[2px] right-0 opacity-70 z-0"
          style="pointer-events: none"
        >
          <img
            src="@/assets/media/images/flames-gif.gif"
            alt="fire"
            class="w-full"
          />
        </div>


        </div>




      </div>

      </div>

    </div>



  </transition-group>

<!-- 👇 Creator Chat - "Member is typing…" bubble -->
      <div
        v-if="isMemberTyping"
        class="flex items-center gap-2 -ml-8 pb-3 mt-1 "
      >
        <img
          :src="memberAvatarSrc"
          alt="Member avatar"
          class="w-12 h-12 rounded-full border-4 border-white"
        />

        <!-- bubble + dots -->
        <div class="relative h-16">
          <img
            :src="typingBubble"
            alt="Typing..."
            class="h-16"
          />

          <div class="absolute inset-0 flex items-center justify-center">
            <div class="flex items-center gap-1">
              <span class="w-2.5 h-2.5 rounded-full bg-zinc-500 typing-dot typing-dot-1"></span>
              <span class="w-2.5 h-2.5 rounded-full bg-zinc-400 typing-dot typing-dot-2"></span>
              <span class="w-2.5 h-2.5 rounded-full bg-zinc-300 typing-dot typing-dot-3"></span>
            </div>
          </div>
        </div>
      </div>



    
  </div>
</div>
</div>

  <!-- Input Area -->
  <div
  v-if="props.member"
    class="bottom-2 py-1 pb-1 px-2 mx-1 sm:mx-0 mt-2 sm:flex order-last
         border-2 z-10 border-[#cae7ff] rounded-md items-end
         fixed sm:relative left-2 right-2 sm:left-auto sm:right-auto bg-white"
  >
    <!-- Chat Input -->
    <div class="flex-grow">
      <!-- Hidden file input for selecting images -->
      <input
          ref="fileInput"
          type="file"
          accept="
            image/*,
            video/*,
            application/pdf,
            text/plain,
            audio/mpeg,
            audio/*,
            application/msword,
            application/vnd.openxmlformats-officedocument.wordprocessingml.document,
            application/vnd.ms-excel,
            application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,
            text/csv,
            application/zip,
            .doc,.docx,.xls,.xlsx,.csv,.zip
          "
          multiple
          class="hidden"
          @change="handleFileChange"
        />


      <!-- ✏️ Editing banner (only when editing an existing message) -->
      <div
        v-if="isEditingMode"
        class="mb-2 flex items-center justify-between gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2"
      >
        <div class="text-sm text-blue-700 font-semibold truncate">
          Editing message
        </div>

        <div class="flex items-center gap-2 shrink-0">
          <button
            type="button"
            class="text-sm font-semibold px-3 py-1 rounded-md bg-blue-600 text-white hover:bg-blue-500"
            @click="saveEditedMessage()"
          >
            Save
          </button>

          <button
            type="button"
            class="text-sm font-semibold px-3 py-1 rounded-md bg-zinc-600 text-white hover:bg-zinc-500"
            @click="cancelEditing()"
          >
            Cancel
          </button>
        </div>
      </div>


      <textarea
      v-model="newMessage"
      @keydown.enter.exact.prevent="handleEnterPress"
      rows="1"
      class="w-full p-2 mb-0 border-none rounded-lg focus:outline-none focus:border-transparent focus:ring-0 placeholder:pl-1 resize-none overflow-hidden"
      :placeholder="
        audioURL
          ? ''
          : selfDestructTime
            ? 'Begin sending your message using self-destruct'
            : (props.member
                ? `Message ${props.member.profileName || props.member.name}`
                : 'Message'
              )
      "
      :class="selfDestructTime ? 'placeholder-red-500' : ''"
      autofocus
      @input="handleTextareaInput"
      ref="textarea"
    />
<p v-if="attachmentsError" class="mt-1 text-[11px] text-red-500">
    {{ attachmentsError }}
  </p>
<!-- Pending attachments preview -->
<div v-if="pendingAttachments.length" class="mt-2">



    <!-- 🔊 stacked audio players -->
    <div
      v-if="pendingAttachments.some(att => att.type === 'audio')"
      class="flex flex-col gap-2 items-start mb-2"
    >
      <template v-for="att in pendingAttachments" :key="att.id + '-audio'">
        <div
          v-if="att.type === 'audio'"
          class="relative w-full sm:w-[260px] xl:w-[360px] rounded-xl "
        >
          <!-- full-width custom audio player -->
          <CreatorChatVoiceMemoPlayer
            v-if="
              (att.mime && att.mime === 'audio/webm') ||
              (att.name && att.name.toLowerCase().includes('voice memo')) ||
              att.isVoiceMemo
            "
            :src="att.previewUrl || att.url || att.data"
          />
          <CreatorChatMp3Player
            v-else
            :src="att.previewUrl || att.url || att.data"
            :name="att.name"
          />

          <!-- ❌ remove button INSIDE top-right of player -->
          <button
            class="absolute -top-2 -right-2 bg-zinc-700 hover:bg-zinc-500 w-7 h-7 rounded-full shadow-sm
                  transition duration-200 ease-in-out transform hover:scale-105
                  disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
            @click="clearPendingAttachment(att)"
            type="button"
            :disabled="att.uploading"
          >
            <XMarkIcon class="w-4 h-4 text-zinc-300" />
          </button>

          <!-- ⏳ blur+spinner hugging the player EXACTLY -->
          <div
            v-if="att.uploading"
            class="absolute inset-0 flex items-center justify-center
                  bg-zinc-900/40 backdrop-blur-[1px] rounded-xl pointer-events-none"
          >
            <div class="relative flex items-center justify-center">
              <div class="absolute inset-0 rounded-full border-4 border-[#6831ff33]"></div>
              <div class="w-8 h-8 animate-spin rounded-full border-4 border-[#6831ff] border-t-transparent"></div>
            </div>


          </div>
        </div>
      </template>
    </div>




  <!-- 🖼️ original grid for NON-audio attachments (unchanged look) -->
  <div
    v-if="pendingAttachments.some(att => att.type !== 'audio')"
    class="flex flex-wrap gap-3 items-start"
  >
    <template v-for="att in pendingAttachments" :key="att.id + '-non-audio'">
      <div
        v-if="att.type !== 'audio'"
        class="relative inline-block w-40 h-40"
      >
        <!-- bounded preview box -->
        <div
          class="w-full h-full rounded-md border bg-white flex items-center justify-center overflow-hidden"
        >
          <!-- image preview -->
          <img
            v-if="att.type === 'image'"
            :src="att.previewUrl"
            class="max-w-full max-h-full object-contain"
            alt="Pending upload"
          />

          <!-- mini video preview -->
          <video
            v-else-if="att.type === 'video'"
            :src="att.previewUrl"
            class="max-w-full max-h-full object-contain"
            controls
          ></video>

          <!-- mini PDF preview -->
          <div
            v-else-if="att.type === 'pdf'"
            class="relative w-full h-full"
          >
            <iframe
              :src="buildPdfViewerSrc(att.previewUrl)"
              class="w-full h-full pointer-events-none border-0"
            ></iframe>
            <div
              class="absolute top-1 left-1 bg-red-500/80 text-[10px] font-semibold text-white px-1 rounded"
            >
              PDF
            </div>
          </div>

          <!-- text file preview -->
          <div
            v-else-if="att.type === 'text'"
            class="flex flex-col items-center justify-center p-2 text-center w-full h-full"
          >
            <img
              class="w-20 mb-1"
              src="@/assets/media/images/chat-txt-doc-icon.svg"
              alt="text document icon"
            />
            <div class="text-[8px] text-zinc-700 px-1 truncate w-full">
              {{ att.name || 'Text file' }}
            </div>
          </div>

           <!-- word doc preview -->
          <div
            v-else-if="att.type === 'word'"
            class="flex flex-col items-center justify-center p-2 text-center w-full h-full"
          >
            <img
              class="w-20 mb-1"
              src="@/assets/media/images/chat-word-doc-icon.svg"
              alt="Word document icon"
            />
            <div class="text-[8px] text-zinc-700 px-1 truncate w-full">
              {{ att.name || 'Word document' }}
            </div>
          </div>

          <!-- excel file preview -->
          <div
            v-else-if="att.type === 'excel'"
            class="flex flex-col items-center justify-center p-2 text-center w-full h-full"
          >
            <img
              class="w-20 mb-1"
              src="@/assets/media/images/chat-excel-doc-icon.svg"
              alt="Excel document icon"
            />
            <div class="text-[8px] text-zinc-700 px-1 truncate w-full">
              {{ att.name || 'Excel file' }}
            </div>
          </div>

          <!-- csv file preview -->
          <div
            v-else-if="att.type === 'csv'"
            class="flex flex-col items-center justify-center p-2 text-center w-full h-full"
          >
            <img
              class="w-20 mb-1"
              src="@/assets/media/images/chat-csv-doc-icon.svg"
              alt="CSV file icon"
            />
            <div class="text-[8px] text-zinc-700 px-1 truncate w-full">
              {{ att.name || 'CSV file' }}
            </div>
          </div>

          <!-- zip archive preview -->
          <div
            v-else-if="att.type === 'zip'"
            class="flex flex-col items-center justify-center p-2 text-center w-full h-full"
          >
            <img
              class="w-20 mb-1"
              src="@/assets/media/images/chat-zip-doc-icon.svg"
              alt="ZIP archive icon"
            />
            <div class="text-[8px] text-zinc-700 px-1 truncate w-full">
              {{ att.name || 'ZIP archive' }}
            </div>
          </div>

        </div>

        <!-- ❌ remove button (original position for non-audio) -->
        <button
          class="absolute -top-2 -right-2 bg-zinc-700 hover:bg-zinc-500 w-7 h-7 rounded-full shadow-sm
                 transition duration-200 ease-in-out transform hover:scale-105
                 disabled:opacity-40 disabled:cursor-not-allowed"
          @click="clearPendingAttachment(att)"
          type="button"
          :disabled="att.uploading"
        >
          <XMarkIcon class="w-4 h-4 text-zinc-300 mx-auto" />
        </button>

        <!-- ⏳ original blur+spinner for non-audio -->
        <div
          v-if="att.uploading"
          class="absolute inset-0 flex items-center justify-center
                 bg-zinc-900/40 backdrop-blur-[1px] rounded-md"
        >
          <div class="relative flex items-center justify-center mt-2">
            <div class="absolute inset-0 rounded-full border-4 border-[#6831ff33]"></div>
            <div class="size-10 animate-spin rounded-full border-4 border-[#6831ff] border-t-transparent"></div>
          </div>
        </div>

      </div>
    </template>
  </div>

  <!-- small meta + error (unchanged) -->
  <div class="mt-1 text-[11px] text-zinc-600">
    <span v-if="pendingAttachments.length === 1">
      {{ pendingAttachments[0].name || '1 attachment selected' }}
    </span>
    <span v-else>
      {{ pendingAttachments.length }} attachments selected
    </span>
  </div>

  <div class="mt-1 text-[11px] text-zinc-400">
  Maximum file size: {{ MAX_ATTACHMENT_MB }} MB per attachment.
</div>
  <p v-if="attachmentsError" class="mt-1 text-[11px] text-red-500">
    {{ attachmentsError }}
  </p>
</div>


      <!-- Mini Player Component -->
      <div v-if="Array.isArray(voiceMemos) && voiceMemos.length > 0">
        <CreatorChatVoiceMemoPlayer
          v-for="memo in voiceMemos"
          :key="memo.id || memo.url"
          :src="memo.url"
          :show-delete="true"
          @delete="deleteAudio(memo)"
        />
      </div>




    </div>

    <!-- chat input speciality items -->

    <div class="flex justify-between items-center gap-2 h-10 pb-2">
      <!-- Send Button -->
      <div class="relative flex items-center">
        <!-- Active Self-Destruct Icon -->
        <div v-if="selfDestructTime">
          <FireIcon
            @mouseenter="showActiveSelfDestructTooltip = true"
            @mouseleave="showActiveSelfDestructTooltip = false"
            class="text-red-600 w-7 h-7 cursor-pointer"
          />

          <!-- Active Self-Destruct tooltip -->
          <div
            v-if="showActiveSelfDestructTooltip"
            class="absolute text-center w-28 bottom-full p-2 -translate-x-12 mb-3 bg-black opacity-60 text-white text-sm rounded tooltip z-10"
          >
            Your message will self-destruct in {{ secondsToReadableTime(selfDestructTime) }}
            <div
              class="tooltip-arrow absolute left-1/2 -right-1 mt-[6px] w-0 h-0 border-l-[12px] border-transparent border-b-[12px] transform -translate-y-1/2 rotate-45 rounded bg-black"
            ></div>
          </div>
        </div>

        <button
          @mouseenter="showSendMessageTooltip = true"
          @mouseleave="showSendMessageTooltip = false"
          @click="handleSend"
          class="p-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 text-[#2d8bffce]">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
          </svg>
        </button>
        <!-- Send Message tooltip -->
        <div
          v-if="showSendMessageTooltip"
          class="absolute text-center w-28 bottom-full p-2 -translate-x-12 mb-3 bg-black opacity-60 text-white text-sm rounded tooltip z-10"
        >
          Send Message
          <div
            class="tooltip-arrow absolute left-1/2 -right-1 mt-[6px] w-0 h-0 border-l-[12px] border-transparent border-b-[12px] transform -translate-y-1/2 rotate-45 rounded bg-black"
          ></div>
        </div>
      </div>

      <!-- Emoji Icon -->
      <div class="relative">
        <FaceSmileIcon
          @mouseenter="showChatEmojiTooltip = true"
          @mouseleave="showChatEmojiTooltip = false"
          @click="toggleEmojiPicker"
          class="text-zinc-600 w-7 h-7 cursor-pointer"
        />

        <!-- Emoji tooltip -->
        <div
          v-if="showChatEmojiTooltip"
          class="absolute text-center w-28 bottom-full p-2 -translate-x-12 mb-3 bg-black opacity-60 text-white text-sm rounded tooltip z-10"
        >
          Emoji
          <div
            class="tooltip-arrow absolute left-1/2 -right-1 mt-[6px] w-0 h-0 border-l-[12px] border-transparent border-b-[12px] transform -translate-y-1/2 rotate-45 rounded bg-black"
          ></div>
        </div>

        <!-- Emoji Picker -->
        <div v-if="showEmojiPicker" class="emoji-picker-container absolute bottom-[60px] right-8 z-30">
          <EmojiPicker @select="addEmoji" />
        </div>
      </div>

      <!-- Attach File Icon -->
      <div class="relative">
        <PaperClipIcon
          @click="fileInput && fileInput.click()"
          @mouseenter="showAttachFileTooltip = true"
          @mouseleave="showAttachFileTooltip = false"
          class="text-zinc-600 w-7 h-7 cursor-pointer"
        />

        <!-- Attach File tooltip -->
        <div
          v-if="showAttachFileTooltip"
          class="absolute text-center w-28 bottom-full p-2 -translate-x-12 mb-3 bg-black opacity-60 text-white text-sm rounded tooltip z-10"
        >
          Upload from Computer
          <div
            class="tooltip-arrow absolute left-1/2 -right-1 mt-[6px] w-0 h-0 border-l-[12px] border-transparent border-b-[12px] transform -translate-y-1/2 rotate-45 rounded bg-black"
          ></div>
        </div>
      </div>

      <!-- Video Message Icon -->
      <div class="relative">
        <VideoCameraIcon
          @click="toggleVideoModal"
          @mouseenter="showVideoMessageTooltip = true"
          @mouseleave="showVideoMessageTooltip = false"
          class="text-zinc-600 w-7 h-7 cursor-pointer"
        />

        <!-- Video record tooltip -->
        <div
          v-if="showVideoMessageTooltip"
          class="absolute text-center w-28 bottom-full p-2 -translate-x-12 mb-3 bg-black opacity-60 text-white text-sm rounded tooltip z-10"
        >
          Record Video Message
          <div
            class="tooltip-arrow absolute left-1/2 -right-1 mt-[6px] w-0 h-0 border-l-[12px] border-transparent border-b-[12px] transform -translate-y-1/2 rotate-45 rounded bg-black"
          ></div>
        </div>
      </div>

      <!-- Voice Message Icon -->
      <div class="relative">
        <MicrophoneIcon
          @click="showVoiceMemoOverlay"
          @mouseenter="showVoiceMessageTooltip = true"
          @mouseleave="showVoiceMessageTooltip = false"
          class="text-zinc-600 w-7 h-7 cursor-pointer"
        />
        <!-- Voice Message tooltip -->
        <div
          v-if="showVoiceMessageTooltip"
          class="absolute text-center w-28 bottom-full p-2 -translate-x-12 mb-3 bg-black opacity-60 text-white text-sm rounded tooltip z-10"
        >
          Record Voice Message
          <div
            class="tooltip-arrow absolute left-1/2 -right-1 mt-[6px] w-0 h-0 border-l-[12px] border-transparent border-b-[12px] transform -translate-y-1/2 rotate-45 rounded bg-black"
          ></div>
        </div>
      </div>

      <!-- Self-Destruct Icon -->
      <div class="relative">
        <ClockIcon
          @click.stop="toggleSelfDestructMenu"
          @mouseenter="showSelfDestructTooltip = true"
          @mouseleave="handleSelfDestructIconMouseLeave"
          class="text-zinc-600 w-7 h-7 cursor-pointer"
        />

        <!-- Self-Destruct tooltip -->
        <div
          v-if="showSelfDestructTooltip && !showSelfDestructMenu"
          class="absolute text-center w-28 bottom-full p-2 -translate-x-12 mb-3 bg-black opacity-60 text-white text-sm rounded tooltip z-10"
        >
          Self-Destructing Message
          <div
            class="tooltip-arrow absolute left-1/2 -right-1 mt-[6px] w-0 h-0 border-l-[12px] border-transparent border-b-[12px] transform -translate-y-1/2 rotate-45 rounded bg-black"
          ></div>
        </div>
      </div>


      <!-- Self-Destruct Menu -->
      <div
        v-if="showSelfDestructMenu"
        class="absolute bottom-[60px] right-0 bg-white border border-gray-300 rounded-md p-2 z-30"
        @mouseenter="cancelHideSelfDestructMenu"
        @mouseleave="handleSelfDestructMenuMouseLeave"
      >
        <div
          @click="() => { clearSelfDestructTime(); showSelfDestructMenu = false; }"
          class="cursor-pointer hover:bg-gray-200 p-2 rounded-md"
        >
          Off
        </div>
        <div
          @click="() => { setSelfDestructTime(10); showSelfDestructMenu = false; }"
          class="cursor-pointer hover:bg-gray-200 p-2 rounded-md"
        >
          10 sec.
        </div>
        <div
          @click="() => { setSelfDestructTime(30); showSelfDestructMenu = false; }"
          class="cursor-pointer hover:bg-gray-200 p-2 rounded-md"
        >
          30 sec.
        </div>
        <div
          @click="() => { setSelfDestructTime(60); showSelfDestructMenu = false; }"
          class="cursor-pointer hover:bg-gray-200 p-2 rounded-md"
        >
          1 min.
        </div>
        <div
          @click="() => { setSelfDestructTime(300); showSelfDestructMenu = false; }"
          class="cursor-pointer hover:bg-gray-200 p-2 rounded-md"
        >
          5 min.
        </div>
        <div
          @click="() => { setSelfDestructTime(3600); showSelfDestructMenu = false; }"
          class="cursor-pointer hover:bg-gray-200 p-2 rounded-md"
        >
          1 hour
        </div>
        <div
          @click="() => { setSelfDestructTime(86400); showSelfDestructMenu = false; }"
          class="cursor-pointer hover:bg-gray-200 p-2 rounded-md"
        >
          1 day
        </div>
        <div
          @click="() => { setSelfDestructTime(604800); showSelfDestructMenu = false; }"
          class="cursor-pointer hover:bg-gray-200 p-2 rounded-md"
        >
          1 week
        </div>
        <div
          @click="() => { setSelfDestructTime(2419200); showSelfDestructMenu = false; }"
          class="cursor-pointer hover:bg-gray-200 p-2 rounded-md"
        >
          4 weeks
        </div>
      </div>


    </div>


  </div>

  
</div>
</template>

<script setup>

  import {
  defineProps,
  defineEmits,
  onMounted,
  onUnmounted,
  ref,
  nextTick,
  watch,
  computed,
} from 'vue';
import { useWebsocketStore } from '@/stores/websocketStore';
import CreatorChatLinkPreview from '@/Components/CreatorChatLinkPreview.vue';
import incomingMessageSound from '@/assets/media/sounds/platforma_wire_chat_sound.mp3';
import CreatorChatMp3Player from '@/Components/CreatorChatMp3Player.vue';
import CreatorChatVoiceMemoRecorder from '@/Components/CreatorChatVoiceMemoRecorder.vue';
import CreatorChatVoiceMemoPlayer from '@/Components/CreatorChatVoiceMemoPlayer.vue';
import CreatorChatInlineVideoPlayer from '@/Components/CreatorChatInlineVideoPlayer.vue';
import CreatorChatVideoMemoRecorder from '@/Components/CreatorChatVideoMemoRecorder.vue';
import CreatorChatSearchOverlay from '@/Components/CreatorChatSearchOverlay.vue';

import defaultAvatar from '@/assets/media/images/default-avatar.png';
import typingBubble from '@/assets/media/images/platforma-chat-bubble.png';


import EmojiPicker from 'vue3-emoji-picker';
import 'vue3-emoji-picker/css';
import { 
  FaceSmileIcon,
  PaperClipIcon,
  VideoCameraIcon,
  MicrophoneIcon,
  ClockIcon,
  EllipsisHorizontalIcon,
  TrashIcon,
  PencilIcon,
  XMarkIcon,
  FireIcon, 
  ArrowDownTrayIcon,
  ChevronDoubleLeftIcon
} from '@heroicons/vue/24/outline';

// Max file size: 500 MB per attachment
const MAX_ATTACHMENT_MB = 500;
const MAX_ATTACHMENT_BYTES = MAX_ATTACHMENT_MB * 1024 * 1024;

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;

const buildChatVideoPoster = (att) => {
  if (!att) return '';

  // Backend may have already given us a poster / thumbnail URL
  if (att.posterUrl) {
    // If it’s already an absolute URL, just use it
    if (/^https?:\/\//i.test(att.posterUrl)) {
      return att.posterUrl;
    }

    // If backend gave us something like "/api/chat-videos/thumbnail/..."
    if (att.posterUrl.startsWith('/api/')) {
      return apiProxyUrl
        ? `${apiProxyUrl}${att.posterUrl}`
        : att.posterUrl;
    }

    // Fallback: treat as-is
    return att.posterUrl;
  }

  if (att.thumbnailUrl) {
    // Same logic for thumbnailUrl
    if (/^https?:\/\//i.test(att.thumbnailUrl)) {
      return att.thumbnailUrl;
    }
    if (att.thumbnailUrl.startsWith('/api/')) {
      return apiProxyUrl
        ? `${apiProxyUrl}${att.thumbnailUrl}`
        : att.thumbnailUrl;
    }
    return att.thumbnailUrl;
  }

  // Otherwise, build from mediaFileId / fileId / id
  const fid = att.mediaFileId || att.fileId || att.id;
  if (!fid) return '';

  // 🔥 This is how *your* app hits the proxy:
  // GET {apiProxyUrl}/api/chat-videos/thumbnail/:fileId
  if (apiProxyUrl) {
    return `${apiProxyUrl}/api/chat-videos/thumbnail/${encodeURIComponent(fid)}`;
  }

  // Dev fallback if apiProxyUrl is not set
  return `/api/chat-videos/thumbnail/${encodeURIComponent(fid)}`;
};


// Props definition with default empty arrays to avoid undefined errors
const props = defineProps({
  member: {
    type: Object,
    default: null
  },
  // 🔹 new prop to know which chat feature instance we’re in
  featureInstanceId: {
    type: [Number, String],
    default: null,
  },

  featureAvatarUrl: {
    type: String,
    default: null,
  },

  open: Boolean,
  showSearch: Boolean,
  searchImages: {
    type: Array,
    default: () => []
  },
  searchLinks: {
    type: Array,
    default: () => []
  },
  searchFiles: {
    type: Array,
    default: () => []
  }
});


// ⬅️ Real emit definition
const emit = defineEmits([
  'update:open',
  'toggleSearch',
  'member-typing', 
]);

// Component references
const chatContainer = ref(null);
const textarea = ref(null);
const suppressNextScroll = ref(false);

// Chat data
const messages = ref([]);
const newMessage = ref('');

// 🔹 pagination / infinite scroll state
const pageSize = 20;                 // how many messages per page
const hasMoreAbove = ref(false);     // whether older messages exist
const isLoadingOlder = ref(false);   // loading older page
const oldestCursor = ref(null);      // message_id of oldest currently loaded message
const initialLoadDone = ref(false);

// 🔥 attachment state (image only for now)
// const pendingAttachment = ref(null); // { type, key, mime, name, size }
// multiple image attachments (max 4)
const pendingAttachments = ref([]); // [{ id, previewUrl, type, key, mime, name, size, uploading }]
const isUploadingAttachment = ref(false);
const uploadInProgressCount = ref(0);
const attachmentsError = ref('');

// ✏️ edit-in-main-input mode
const editingMessageId = ref(null);
const editingOriginalText = ref('');
const isEditingMode = computed(() => !!editingMessageId.value);

const highlightedMessageId = ref(null);

const websocketStore = useWebsocketStore();
let unsubscribeChatNewMessage = null;

let unsubscribeChatUpdated = null;

const isMemberTyping = ref(false);
const typingTimeoutId = ref(null);
const TYPING_TIMEOUT_MS = 18000; // how long to show bubble after last ping

const lastTypingSentAt = ref(0);
const TYPING_THROTTLE_MS = 1500; // minimum gap between "typing" pings

let unsubscribeChatTyping = null;

const clearMemberTyping = () => {
  // Turn off the bubble
  isMemberTyping.value = false;

  // Clear any pending timeout
  if (typingTimeoutId.value) {
    clearTimeout(typingTimeoutId.value);
    typingTimeoutId.value = null;
  }
};


// 🔊 incoming message sound
let incomingMessageAudio = null;

const initIncomingMessageAudio = () => {
  if (incomingMessageAudio) return;
  incomingMessageAudio = new Audio(incomingMessageSound);
  incomingMessageAudio.preload = 'auto';
};

const playIncomingMessageSound = () => {
  if (!incomingMessageAudio) return;

  try {
    incomingMessageAudio.currentTime = 0; // rewind if it was mid-play
    incomingMessageAudio.play().catch((err) => {
      console.warn('[Chat] Unable to play incoming message sound:', err);
    });
  } catch (err) {
    console.warn('[Chat] Error triggering sound:', err);
  }
};

// Conversation state
// 🔹 Track which member's conversation is active
const activeMemberId = ref(null);
const isLoadingMessages = ref(false);
const messagesError = ref(null);

const normalizeAvatarSrc = (rawValue) => {
  const raw = String(rawValue || '').trim();
  if (!raw) return defaultAvatar;

  const lower = raw.toLowerCase();
  if (
    lower.includes('/media/images/default-avatar.png') ||
    lower.includes('src/assets/media/images/default-avatar.png') ||
    lower.endsWith('/default-avatar.png') ||
    lower === 'default-avatar.png'
  ) {
    return defaultAvatar;
  }

  return raw;
};

const creatorAvatarSrc = computed(() =>
  normalizeAvatarSrc(props.featureAvatarUrl)
);

const creatorAvatarUrl = computed(() => creatorAvatarSrc.value);

const memberAvatarSrc = computed(() =>
  normalizeAvatarSrc(props.member?.avatar)
);

const handleMessageAvatarError = (event) => {
  const img = event?.target;
  if (!img) return;
  img.src = defaultAvatar;
};

// Is this message from the member (vs creator)?
const isMemberMessage = (message) => {
  if (!message) return false;

  // Explicit senderType from backend / local messages
  if (message.senderType === 'member') return true;
  if (message.senderType === 'creator') return false;

  // Fallback: compare username to current member name
  const memberName = props.member?.profileName || props.member?.name;
  if (memberName && message.username === memberName) return true;

  // Messages from "You" are creator-side
  if (message.username === 'You') return false;

  return false;
};

// Pick avatar based on sender
const avatarForMessage = (message) => {
  if (!message) return creatorAvatarSrc.value;

  if (message.senderType === 'member') {
    return memberAvatarSrc.value;
  }

  if (message.senderType === 'creator') {
    return creatorAvatarSrc.value;
  }

  const memberName = props.member?.profileName || props.member?.name;
  if (memberName && message.username === memberName) {
    return memberAvatarSrc.value;
  }

  if (message.username === 'You') {
    return creatorAvatarSrc.value;
  }

  // default fallback
  return creatorAvatarSrc.value;
};

const splitLineIntoSegments = (line) => {
  if (!line) return [];

  const regex = /(https?:\/\/[^\s]+)/gi;
  const segments = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(line)) !== null) {
    // Text before the URL
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        value: line.slice(lastIndex, match.index),
      });
    }

    // The URL itself
    segments.push({
      type: 'link',
      value: match[0],
    });

    lastIndex = match.index + match[0].length;
  }

  // Remaining text after last URL
  if (lastIndex < line.length) {
    segments.push({
      type: 'text',
      value: line.slice(lastIndex),
    });
  }

  return segments;
};


// Build a PDF iframe src that:
// - forces page 1
// - tries to hide toolbar/nav/scrollbars in the built-in viewer
const buildPdfViewerSrc = (src) => {
  if (!src) return '';

  // strip existing hash so we don’t stack options
  const hashIndex = src.indexOf('#');
  const base = hashIndex === -1 ? src : src.slice(0, hashIndex);

  const options = 'page=1&zoom=page-width&toolbar=0&navpanes=0&scrollbar=0';

  return `${base}#${options}`;
};

const showTextOverlay = async (url, name) => {
  if (!url) return;

  isImageOverlayOpen.value = true;
  overlayType.value = 'text';
  currentFileName.value = name || '';
  currentImageSrc.value = url; // reuse as “source URL” for download

  overlayTextContent.value = '';
  overlayError.value = '';
  overlayLoading.value = true;

  try {
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
    // If your backend sets a different content-type but still returns text,
    // this will still work:
    const text = await res.text();

    // Optional: hard cap extremely large files
    const MAX_CHARS = 100_000;
    overlayTextContent.value =
      text.length > MAX_CHARS
        ? text.slice(0, MAX_CHARS) + '\n\n[Truncated in viewer…]'
        : text;
  } catch (err) {
    console.error('Failed to load text file', err);
    overlayError.value = 'Unable to load this text file. You can still download it.';
  } finally {
    overlayLoading.value = false;
  }
};


// Tooltips
const showSendMessageTooltip = ref(false);
const showChatEmojiTooltip = ref(false);
const showAttachFileTooltip = ref(false);
const showVideoMessageTooltip = ref(false);
const showVoiceMessageTooltip = ref(false);
const showSelfDestructTooltip = ref(false);
const showActiveSelfDestructTooltip = ref(false);


// Emoji Picker
const showEmojiPicker = ref(false);

// Constants and computed values

const loadConversation = async (memberId, featureInstanceId) => {
  if (!memberId || !featureInstanceId) {
    messages.value = [];
    hasMoreAbove.value = false;
    oldestCursor.value = null;
    initialLoadDone.value = false;
    return;
  }

  isLoadingMessages.value = true;
  isLoadingOlder.value = false;
  messagesError.value = null;
  hasMoreAbove.value = false;
  oldestCursor.value = null;
  initialLoadDone.value = false;

  try {
    const url = new URL(`${apiProxyUrl}/api/chat-messages`);
    url.searchParams.append('memberId', String(memberId));
    url.searchParams.append('featureInstanceId', String(featureInstanceId));
    url.searchParams.append('limit', String(pageSize));

    const response = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      if (response.status === 404) {
        messages.value = [];
        hasMoreAbove.value = false;
        oldestCursor.value = null;
        return;
      }
      throw new Error(`Failed to fetch messages: ${response.status}`);
    }

    const data = await response.json();
    const apiMessages = data.messages || [];
    const paging = data.paging || {};

    const uiMessages = apiMessages.map(mapApiMessageToUi);

    uiMessages.sort((a, b) => {
      const da = new Date(a.date);
      const db = new Date(b.date);
      return da - db;
    });

    // ⬇️ first load: let watcher skip auto-scroll
    suppressNextScroll.value = true;
    messages.value = uiMessages;

    if (uiMessages.length) {
      hasMoreAbove.value = !!paging.hasMore;

      if (hasMoreAbove.value && paging.nextCursor) {
        oldestCursor.value = paging.nextCursor;
      } else {
        oldestCursor.value = uiMessages[0].id || null;
      }
    } else {
      hasMoreAbove.value = false;
      oldestCursor.value = null;
    }
  } catch (err) {
    console.error('Error loading conversation:', err);
    messagesError.value = err.message || 'Failed to load messages';
    messages.value = [];
    hasMoreAbove.value = false;
    oldestCursor.value = null;
  } finally {
    isLoadingMessages.value = false;
    initialLoadDone.value = true;

    // ⬇️ actually *put* us at the bottom, instantly (no visible yank)
    if (messages.value && messages.value.length > 0) {
      // ⬇️ NEW: start stick-to-bottom window
      startStickToBottom();
      scrollToBottom({ instant: true });
    }
  }
};


const loadOlderMessages = async () => {
  if (
    isLoadingOlder.value ||
    isLoadingMessages.value ||
    !hasMoreAbove.value ||
    !props.member ||
    !props.featureInstanceId ||
    !oldestCursor.value
  ) {
    return;
  }

  const memberId = props.member.id;
  const featureInstanceId = props.featureInstanceId;

  isLoadingOlder.value = true;

  const container = chatContainer.value;
  const prevScrollHeight = container ? container.scrollHeight : 0;
  const prevScrollTop = container ? container.scrollTop : 0;

  try {
    const url = new URL(`${apiProxyUrl}/api/chat-messages`);
    url.searchParams.append('memberId', String(memberId));
    url.searchParams.append('featureInstanceId', String(featureInstanceId));
    url.searchParams.append('limit', String(pageSize));
    url.searchParams.append('beforeId', String(oldestCursor.value));

    const response = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      if (response.status === 404) {
        hasMoreAbove.value = false;
        return;
      }
      throw new Error(`Failed to fetch older messages: ${response.status}`);
    }

    const data = await response.json();
    const apiMessages = data.messages || [];
    const paging = data.paging || {};

    if (!apiMessages.length) {
      hasMoreAbove.value = false;
      return;
    }

    const olderUi = apiMessages.map(mapApiMessageToUi);

    olderUi.sort((a, b) => {
      const da = new Date(a.date);
      const db = new Date(b.date);
      return da - db;
    });

    // ⬇️ prepend older messages, but tell the watcher NOT to snap to bottom
    suppressNextScroll.value = true;
    messages.value = [...olderUi, ...messages.value];

    hasMoreAbove.value = !!paging.hasMore;
    if (hasMoreAbove.value && paging.nextCursor) {
      oldestCursor.value = paging.nextCursor;
    } else {
      oldestCursor.value = olderUi[0].id || oldestCursor.value;
    }

    await nextTick();

    if (container) {
      const newScrollHeight = container.scrollHeight;

      // ⬇️ temporarily disable smooth scroll while we correct scrollTop
      const prevBehavior = container.style.scrollBehavior;
      container.style.scrollBehavior = 'auto';

      container.scrollTop =
        newScrollHeight - prevScrollHeight + prevScrollTop;

      // force reflow, then restore
      container.getBoundingClientRect();
      container.style.scrollBehavior = prevBehavior || '';
    }
  } catch (err) {
    console.error('Error loading older messages:', err);
  } finally {
    isLoadingOlder.value = false;
  }
};

const ensureMessageInBuffer = async (targetId) => {
  const targetIdStr = String(targetId);
  let safety = 0;
  const MAX_PAGES = 30; // hard cap to avoid infinite loops

  while (safety < MAX_PAGES) {
    const exists = messages.value.some(
      (m) => String(m.id) === targetIdStr
    );
    if (exists) return true;

    // No more pages above → give up
    if (!hasMoreAbove.value) return false;

    await loadOlderMessages();
    safety += 1;
  }

  // Final check after loop
  return messages.value.some((m) => String(m.id) === targetIdStr);
};

const scrollToMessageById = async (messageId) => {
  await nextTick();

  const container = chatContainer.value;
  if (!container) return;

  const el = container.querySelector(
    `[data-message-id="${messageId}"]`
  );
  if (!el) return;

  const containerRect = container.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();

  // Move scroll so the message is roughly centered
  const offset = elRect.top - containerRect.top;
  container.scrollTop +=
    offset - container.clientHeight / 2 + el.clientHeight / 2;
};


const handleSearchJump = async ({ id }) => {
  if (!id) return;

  // Do not auto-stick-to-bottom while we’re jumping
  stickToBottomUntil.value = 0;
  suppressNextScroll.value = true;

  // Make sure the message is actually in messages[] (page up if needed)
  const found = await ensureMessageInBuffer(id);
  if (!found) {
    console.warn(
      '[CreatorChat] Search jump: message not found in buffer',
      id
    );
    return;
  }

  // Scroll to the message
  await scrollToMessageById(id);

  // Briefly highlight it
  highlightedMessageId.value = id;
  setTimeout(() => {
    if (highlightedMessageId.value === id) {
      highlightedMessageId.value = null;
    }
  }, 10000);
};


// fired from @scroll on chatContainer
const onChatScroll = (event) => {
  const el = event?.target || chatContainer.value;
  if (!el) return;

  // 👇 If user manually scrolls away from the bottom,
  // disable sticky auto-scroll so we don't yank them down.
  if (!isUserNearBottom()) {
    stickToBottomUntil.value = 0;
  }

  if (isLoadingOlder.value || !hasMoreAbove.value) return;

  // near the top? load previous page
  const THRESHOLD = 80; // px
  if (el.scrollTop <= THRESHOLD) {
    loadOlderMessages();
  }
};


const scrollToBottom = async (opts = {}) => {
  await nextTick();
  const container = chatContainer.value;
  if (!container) return;

  const { instant = false } = opts;

  if (instant) {
    // temporarily disable smooth behavior
    const prevBehavior = container.style.scrollBehavior;
    container.style.scrollBehavior = 'auto';
    container.scrollTop = container.scrollHeight;
    // force reflow
    container.getBoundingClientRect();
    // restore whatever was there (or Tailwind’s scroll-smooth)
    container.style.scrollBehavior = prevBehavior || '';
  } else {
    container.scrollTop = container.scrollHeight;
  }
};

// 🔹 keep us pinned to the bottom for a short window (handles late-loading images)
const stickToBottomUntil = ref(0);

// Images can come in late (network + decode). Give it real time.
const STICK_BOTTOM_WINDOW_MS = 8000;

// extend window (don’t shorten it)
const startStickToBottom = (ms = STICK_BOTTOM_WINDOW_MS) => {
  stickToBottomUntil.value = Math.max(stickToBottomUntil.value, Date.now() + ms);
};

const shouldStickToBottom = () => Date.now() < stickToBottomUntil.value;

// more reliable than a single scrollTop assignment
const forceScrollBottom = async () => {
  await nextTick();
  const el = chatContainer.value;
  if (!el) return;

  // disable smooth for this correction
  const prevBehavior = el.style.scrollBehavior;
  el.style.scrollBehavior = 'auto';

  el.scrollTop = el.scrollHeight;

  // do it again on next frame to catch layout shift
  requestAnimationFrame(() => {
    el.scrollTop = el.scrollHeight;
    // restore
    el.getBoundingClientRect();
    el.style.scrollBehavior = prevBehavior || '';
  });
};

const handleMessageMediaLoad = async () => {
  // Only care about media loads when user is near the bottom
  if (!isUserNearBottom()) return;

  // every image/video load should extend the stick window
  startStickToBottom(2000);

  if (!shouldStickToBottom()) return;
  await forceScrollBottom();
};


const handleSend = async () => {

  // ✏️ If editing, Save instead of sending a new message
  if (isEditingMode.value) {
    await saveEditedMessage();
    return;
  }

  if (isUploadingAttachment.value) {
    console.log('[MemberChatComponent2] Still uploading attachment, please wait');
    return;
  }

  const hasText = !!(newMessage.value && newMessage.value.trim());
  const hasAttachment = pendingAttachments.value.length > 0;

  if (hasAttachment) {
    console.log('Sending attachments (and maybe text)');
    await sendMessage();
  } else if (hasText && voiceMemos.value.length > 0) {
    console.log('Sending combined text and voice memo');
    sendCombinedMessage(voiceMemos.value);
  } else if (hasText) {
    console.log('Sending text message');
    await sendMessage();
  } else if (voiceMemos.value.length > 0) {
    console.log('Sending voice memo');
    sendVoiceMemo(voiceMemos.value);
  } else {
    console.log('No text or voice memo to send');
  }
};

const mapAttachmentForApi = (attachment) => {
  if (!attachment) return null;
  return {
    type: attachment.type || 'image',
    key: attachment.key || null,
    mime: attachment.mime || null,
    name: attachment.name || null,
    size: typeof attachment.size === 'number' ? attachment.size : null,
    mediaFileId:
      attachment.mediaFileId ||
      attachment.fileId ||
      null,
  };
};


const postChatMessage = async ({ body, attachments }) => {
  if (!props.member || !props.featureInstanceId) {
    console.warn(
      '[MemberChatComponent2] Cannot send – missing member or featureInstanceId',
      {
        member: props.member,
        featureInstanceId: props.featureInstanceId,
      }
    );
    return;
  }

  // 🔹 Normalize attachments → array of clean objects or null
  const attachmentPayload =
    Array.isArray(attachments) && attachments.length
      ? attachments.map(mapAttachmentForApi).filter(Boolean)
      : null;

  const payload = {
    memberId: props.member.id,
    featureInstanceId: props.featureInstanceId,
    body: body && body.trim ? (body.trim() || null) : body || null,
    // 👇 single field that can be NULL, an object, or an ARRAY
    attachment: attachmentPayload,
    // ✅ NEW: send timer for NEW messages (null if not set)
    selfDestructSeconds: selfDestructTime.value || null,
  };

  const response = await fetch(`${apiProxyUrl}/api/chat-messages`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Failed to send message: ${response.status}`);
  }

  let data;
  try {
    data = await response.json();
  } catch (jsonErr) {
    console.warn(
      '[MemberChatComponent2] Response had no JSON body, using local payload only',
      jsonErr
    );
    data = {};
  }

  const saved =
    data && typeof data === 'object'
      ? data.message || data
      : {};

  const baseForUi = {
    ...saved,
    sender_type: saved.sender_type || saved.senderType || 'creator',
    body: payload.body,
    created_at:
      saved.created_at || saved.createdAt || new Date().toISOString(),
  };

  // 👇 Make sure the UI mapper sees the attachments as an array
  if (attachmentPayload) {
    // this matches your mapApiMessageToUi priority (attachment_json / attachments / attachment)
    baseForUi.attachment_json = attachmentPayload;
  }

  const uiMessage = mapApiMessageToUi(baseForUi);

  const wasNearBottom = isUserNearBottom();

messages.value.push(uiMessage);

// On SEND (creator), always pin to bottom.
// On general changes, only if user was already near bottom.
if (uiMessage.senderType === 'creator' || wasNearBottom) {
  startStickToBottom();
  await forceScrollBottom();
}

return uiMessage;

};

const handleVideoMemoSend = async (uploadedMeta) => {
  try {
    // Reuse the unified chat message posting pipeline
    await postChatMessage({
      body: newMessage.value,
      attachments: [uploadedMeta],
    });

    // Clear caption text and reset textarea height
    newMessage.value = '';
    await nextTick();
    resetTextareaSize();
  } catch (err) {
    console.error(
      '[MemberChatComponent2] Failed to send video memo message:',
      err,
    );
  }
};


const sendMessage = async () => {
  console.log('[sendMessage] called with:', {
    member: props.member,
    featureInstanceId: props.featureInstanceId,
    text: newMessage.value,
    attachmentsCount: pendingAttachments.value.length,
  });

  if (!props.member || !props.featureInstanceId) {
    console.warn(
      '[MemberChatComponent2] Cannot send – missing member or featureInstanceId',
      {
        member: props.member,
        featureInstanceId: props.featureInstanceId,
      }
    );
    return;
  }

  const hasBody = !!(newMessage.value && newMessage.value.trim());
  const attachments = [...pendingAttachments.value]; // snapshot

  if (!hasBody && attachments.length === 0) {
    console.warn(
      '[MemberChatComponent2] Cannot send – empty body and no attachment'
    );
    return;
  }

  try {
    // 🔥 ALWAYS a single message:
    //  - text-only: attachment = null
    //  - text + up to 4 images: attachment = ARRAY
    await postChatMessage({
      body: newMessage.value,
      attachments,
    });

     // after sending, clear input + attachments
    newMessage.value = '';
    showEmojiPicker.value = false;

    // revoke previews
    attachments.forEach((att) => {
      if (att.previewUrl) {
        try {
          window.URL.revokeObjectURL(att.previewUrl);
        } catch (e) {
          console.warn('[Chat] Failed to revoke object URL', e);
        }
      }
    });

    pendingAttachments.value = [];
    attachmentsError.value = '';

    if (fileInput.value) fileInput.value.value = '';
    // selfDestructTime is reset in handleSelfDestruct after scheduling
    selfDestructTime.value = null;

    // 🔹 Now that the DOM reflects the cleared input, reset height
    await nextTick();
    resetTextareaSize();
  } catch (err) {
    console.error('Error sending message:', err);
  }
};


const sendVoiceMemo = (memos) => {
  const message = {
    id: messages.value.length + 1,
    username: 'You',
    avatar: '/media/images/default-avatar.png',
    time: new Date().toLocaleTimeString(),
    date: new Date().toLocaleDateString(),
    voiceMemos: memos, // Set the entire array of voice memos
    type: 'voiceMemo',
    showOptions: false,
    deleting: false,
    showFireTooltip: false,
    textContent: null // No text content for pure voice memos
  };

  messages.value.push(message);
  voiceMemos.value = []; // Clear all voice memos after sending

  handleSelfDestruct(message);
};


const sendCombinedMessage = (memos) => {
  const message = {
    id: messages.value.length + 1,
    username: 'You',
    avatar: '/media/images/default-avatar.png',
    time: new Date().toLocaleTimeString(),
    date: new Date().toLocaleDateString(),
    voiceMemos: memos, // Set the entire array of voice memos
    type: 'combined', // Set type to combined
    showOptions: false,
    deleting: false,
    showFireTooltip: false,
    textContent: newMessage.value.trim()
  };

  messages.value.push(message);
  voiceMemos.value = []; // Clear all voice memos after sending
  newMessage.value = '';

  handleSelfDestruct(message);
};

const resizeTextarea = async () => {
  await nextTick();
  textarea.value.style.height = 'auto';
  textarea.value.style.height = `${textarea.value.scrollHeight}px`;
};

const handleTextareaInput = async () => {
  await resizeTextarea();
  maybeSendTyping();
};

const maybeSendTyping = () => {
   // 🚫 Do NOT broadcast typing while in edit mode
  if (isEditingMode.value) {
    return;
  }
  if (!props.member || !props.featureInstanceId) return;

  const now = Date.now();
  if (now - lastTypingSentAt.value < TYPING_THROTTLE_MS) return;

  lastTypingSentAt.value = now;

  // Fire-and-forget HTTP ping to BFF; backend will fan out WS event
  fetch(`${apiProxyUrl}/api/chat-typing`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      memberId: props.member.id,
      featureInstanceId: props.featureInstanceId,
      senderType: 'creator',
      typing: true,
    }),
  }).catch((err) => {
    console.warn('[MemberChatComponent2] typing notify failed:', err);
  });
};

const startEditMessage = async (message) => {
  if (!message || !message.id) return;

  // Only allow editing creator messages (matches your menu visibility)
  if (message.senderType !== 'creator') return;

  // 🔹 Clear any unsent draft attachments / voice memos before entering edit mode
  clearPendingAttachment(); // nukes all attachments + resets file input
  if (Array.isArray(voiceMemos.value) && voiceMemos.value.length > 0) {
    voiceMemos.value = [];
  }

  editingMessageId.value = message.id;
  editingOriginalText.value = message.content || '';
  newMessage.value = editingOriginalText.value;

  // ✅ reset edit-timer tracking
  selfDestructTouchedInEdit.value = false;

  // ✅ prefill selfDestructTime from server selfDestructAt (remaining seconds)
  selfDestructTime.value = null;
  if (message.selfDestructAt) {
    const ts = new Date(message.selfDestructAt).getTime();
    if (!Number.isNaN(ts)) {
      const remaining = Math.ceil((ts - Date.now()) / 1000);
      if (remaining > 0) {
        selfDestructTime.value = remaining;
      }
    }
  }


  // 🔹 Pre-fill existing attachments into pendingAttachments (images + PDFs, max 4)
  const restored = [];

  if (Array.isArray(message.attachments) && message.attachments.length) {

message.attachments.slice(0, 4).forEach((att, idx) => {
  if (!att) return;

  const mime = att.mime || '';
  const inferredType =
    att.type ||
    (mime.startsWith('image/')
      ? 'image'
      : mime.startsWith('video/')
      ? 'video'
      : mime === 'application/pdf'
      ? 'pdf'
      : mime === 'text/plain'
      ? 'text'
      : mime.startsWith('audio/')
      ? 'audio'
      : null);

  if (!inferredType) return;

  let url =
    att.url ||
    (att.key
      ? `${apiProxyUrl}/api/chat-file?key=${encodeURIComponent(att.key)}`
      : null);

  const fileId = att.mediaFileId || att.fileId || null;

  // ✅ If it's a video and we have a fileId, give it a stable reference URL
  if (!url && inferredType === 'video' && fileId) {
    url = `${apiProxyUrl}/api/chat-videos/master/${encodeURIComponent(fileId)}`;
  }

  restored.push({
    id: `edit-${message.id}-${idx}`,
    previewUrl: url,
    type: inferredType,
    key: att.key || null,
    mediaFileId: fileId, // 👈 reuse
    mime: mime || null,
    name: att.name || att.filename || null,
    size: typeof att.size === 'number' ? att.size : null,
    uploading: false,
    fromExisting: true,
  });
});

}
 else if (
    // 🔙 legacy single-image path (message.file / message.type === 'image')
    message.type === 'image' &&
    message.file &&
    message.file.data
  ) {
    let existingKey = null;

    try {
      // message.file.data is something like: `${apiProxyUrl}/api/chat-file?key=...`
      const url = new URL(message.file.data, window.location.origin);
      existingKey = url.searchParams.get('key');
    } catch (e) {
      console.warn('[Chat] Failed to parse existing attachment key from URL', e);
    }

    if (existingKey) {
      restored.push({
        id: `edit-${message.id}`,
        previewUrl: message.file.data,
        type: 'image',
        key: existingKey,
        mime: message.file.mime || null,
        name: message.fileName || null,
        size: typeof message.file.size === 'number' ? message.file.size : null,
        uploading: false,
        fromExisting: true,
      });
    }
  } else if (
    // 🔙 legacy single-PDF path
    message.file &&
    message.file.type === 'pdf' &&
    message.file.data
  ) {
    let existingKey = null;

    try {
      const url = new URL(message.file.data, window.location.origin);
      existingKey = url.searchParams.get('key');
    } catch (e) {
      console.warn('[Chat] Failed to parse existing PDF key from URL', e);
    }

    restored.push({
      id: `edit-${message.id}-pdf`,
      previewUrl: message.file.data,
      type: 'pdf',
      key: existingKey,
      mime: 'application/pdf',
      name: message.fileName || null,
      size: typeof message.file.size === 'number' ? message.file.size : null,
      uploading: false,
      fromExisting: true,
    });
  }

  // 🔥 This is what drives the inline previews in the textarea area
  pendingAttachments.value = restored;

  // Close menu
  message.showOptions = false;

  await nextTick();
  if (textarea.value) textarea.value.focus();
  await resizeTextarea();
};

const cancelEditing = () => {
  // exit edit mode + clear text state
  editingMessageId.value = null;
  editingOriginalText.value = '';
  newMessage.value = '';

  // clear staged attachments (both existing + newly uploaded)
  pendingAttachments.value.forEach((att) => {
    if (att.previewUrl) {
      try {
        window.URL.revokeObjectURL(att.previewUrl);
      } catch (e) {
        console.warn('[MemberChat] Failed to revoke preview URL on cancelEditing', e);
      }
    }
  });
  pendingAttachments.value = [];
  attachmentsError.value = '';

  // reset file input so same file can be re-selected later
  if (fileInput.value) {
    fileInput.value.value = '';
  }

  // reset composer height
  resetTextareaSize();

  selfDestructTouchedInEdit.value = false;
  selfDestructTime.value = null;

};

// ✅ local update helper
const applyEditedTextLocally = (messageId, newText) => {
  const idx = messages.value.findIndex((m) => String(m.id) === String(messageId));
  if (idx === -1) return;

  messages.value[idx] = {
    ...messages.value[idx],
    content: newText,
  };
};

// ✅ server save (uses same pattern as DELETE)
const saveEditedMessage = async () => {
  if (!editingMessageId.value) return;

  if (isUploadingAttachment.value) {
    console.warn('[Chat] Still uploading attachment, cannot save edit yet.');
    return;
  }

  const trimmed = (newMessage.value || '').trim();

  // 🔹 Use ALL current pending attachments (not just [0])
  const attachmentsForSave = pendingAttachments.value
    // ✅ keep attachments that were uploaded via key OR video/audio/etc that use mediaFileId
    .filter((att) => att && (att.key || att.mediaFileId || att.fileId))
    .map(mapAttachmentForApi)
    .filter(Boolean);

  const hasAttachments = attachmentsForSave.length > 0;
  const hasText = trimmed.length > 0;

  // Block truly empty result (no text, no attachments)
  if (!hasText && !hasAttachments) {
    console.warn('[Chat] Cannot save empty message with no attachment.');
    return;
  }

  const messageId = editingMessageId.value;

  // 🔹 If no attachments left, send null to clear on backend.
  //     If 1+, send ARRAY (same shape as postChatMessage).
  const attachmentPayload = hasAttachments ? attachmentsForSave : null;

  // Optimistic UI text update first (feels instant)
  applyEditedTextLocally(messageId, trimmed);

  try {
    // ✅ Build PUT payload (conditionally include selfDestructSeconds)
    const putPayload = {
      body: trimmed,
      attachment: attachmentPayload, // 👈 now array or null
    };

    // ✅ Only include when user actually changed timer while editing
    // - omit field => backend keeps as-is
    // - null => clear self_destruct_at
    // - number => set/change timer
    if (selfDestructTouchedInEdit.value) {
      putPayload.selfDestructSeconds = selfDestructTime.value; // null|number
    }

    const resp = await fetch(`${apiProxyUrl}/api/chat-messages/${messageId}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(putPayload),
    });

    if (!resp.ok) {
      console.warn('[Chat] Edit save failed on server:', resp.status);
      return;
    }

    const contentType = resp.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      return;
    }

    let data;
    try {
      data = await resp.json();
    } catch (err) {
      console.warn('[Chat] Failed to parse edit response JSON:', err);
      return;
    }

    const updatedRow = data?.message || data || null;
    if (!updatedRow) return;

    const ui = mapApiMessageToUi(updatedRow);
    if (!ui) return;

    upsertIncomingUiMessage(ui);
  } catch (err) {
    console.warn('[Chat] Edit save request error:', err);
  } finally {
    // Exit edit mode + clear draft state
    editingMessageId.value = null;
    editingOriginalText.value = '';
    newMessage.value = '';

    // ✅ reset edit-timer tracking + selected time after finishing edit
    selfDestructTouchedInEdit.value = false;
    selfDestructTime.value = null;

    // Clear attachments from input area after saving edit
    clearPendingAttachment();

    await nextTick();
    resetTextareaSize();
  }
};

const toggleEmojiPicker = () => {
  showEmojiPicker.value = !showEmojiPicker.value;
};

const addEmoji = (emoji) => {
  if (emoji && emoji.i) {
    newMessage.value += emoji.i;
  } else {
    console.warn('Unexpected emoji object:', emoji);
  }
};

const deleteMessage = async (message) => {
  const index = messages.value.findIndex((m) => m.id === message.id);
  if (index === -1) return;

  const msg = messages.value[index];
  msg.deleting = true;

  // Only call backend when this is a persisted chat row
  const isPersisted =
    msg.senderType === 'creator' ||
    msg.senderType === 'member' ||
    msg.senderType === 'system';

  try {
    if (isPersisted && msg.id) {
      const resp = await fetch(`${apiProxyUrl}/api/chat-messages/${msg.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!resp.ok) {
        console.error(
          '[MemberChatComponent2] Failed to delete message, status:',
          resp.status
        );
        msg.deleting = false;
        return;
      }
    }

    // UI fade-out you already had
    suppressNextScroll.value = true; // ⬅️ don't auto-scroll for this removal
    setTimeout(() => {
      messages.value.splice(index, 1);
    }, 500);
  } catch (err) {
    console.error('[MemberChatComponent2] Error deleting message:', err);
    msg.deleting = false;
  }
};


const handleEnterPress = ($event) => {
  // If Shift is not pressed, send the message.
  if (!$event.shiftKey) {
    handleSend(); // Changed from sendMessage() to handleSend()
    // To prevent new line in the textarea after sending the message
    $event.preventDefault();
    resetTextareaSize();
  }
};

const resetTextareaSize = () => {
  textarea.value.style.height = 'auto';
};

const splitLines = (text) => {
  if (!text) return [];
  return text.split('\n');
};

// date divider logic
function shouldShowDateDivider(message, index) {
  if (!message?.date) return false;

  // Always show for the first message
  if (index === 0) return true;

  const prev = messages.value[index - 1];
  if (!prev?.date) return true;

  const prevDate = new Date(prev.date);
  const currDate = new Date(message.date);

  if (isNaN(prevDate.getTime()) || isNaN(currDate.getTime())) {
    return false;
  }

  return prevDate.toDateString() !== currDate.toDateString();
}

const parseTimeToMs = (v) => {
  if (v == null) return null;

  if (typeof v === 'number') {
    if (!Number.isFinite(v)) return null;
    return v < 1e12 ? v * 1000 : v; // seconds vs ms
  }

  if (typeof v === 'string') {
    const s = v.trim();
    if (!s) return null;

    if (/^\d+$/.test(s)) {
      const n = Number(s);
      if (!Number.isFinite(n)) return null;
      return n < 1e12 ? n * 1000 : n;
    }

    const t = Date.parse(s);
    return Number.isFinite(t) ? t : null;
  }

  if (v instanceof Date) {
    const t = v.getTime();
    return Number.isFinite(t) ? t : null;
  }

  return null;
};


const mapApiMessageToUi = (row) => {
  if (!row) return null;

  // 🔹 Unwrap possible shapes:
  //   - plain DB row
  //   - { message, meta }
  //   - { chatMessage, meta }
  let base = row;

  if (base.message) base = base.message;
  if (base.chatMessage) base = base.chatMessage;
  if (base.messageRow) base = base.messageRow;

  const createdRaw =
    base.created_at ||
    base.createdAt ||
    base.created ||
    null;

  const created = createdRaw ? new Date(createdRaw) : new Date();

  const senderType = base.sender_type || base.senderType || 'creator';
  const isCreator = senderType === 'creator';

  // 🔹 normalize attachment(s) from ANY possible shape
  let attachments = [];

  // 1) DB / service field: attachment_json OR attachmentJson (string, object, or array)
  const rawAttachmentJson =
    base.attachment_json != null
      ? base.attachment_json
      : base.attachmentJson != null
      ? base.attachmentJson
      : null;

  if (rawAttachmentJson != null) {
    try {
      const parsed =
        typeof rawAttachmentJson === 'string'
          ? JSON.parse(rawAttachmentJson)
          : rawAttachmentJson;

      if (Array.isArray(parsed)) {
        attachments = parsed;
      } else if (parsed) {
        attachments = [parsed];
      }
    } catch (e) {
      console.warn(
        '[Chat] Failed to parse attachment json:',
        rawAttachmentJson,
        e
      );
    }
  }

  // 2) WS payload may already send `attachments` or `attachment`
  if (!attachments.length && Array.isArray(base.attachments)) {
    attachments = base.attachments;
  } else if (!attachments.length && base.attachment) {
    attachments = Array.isArray(base.attachment)
      ? base.attachment
      : [base.attachment];
  }


  // 3) legacy schema (file_key / file_type / mime_type / file_name / file_size)
  if (!attachments.length && (base.file_key || base.fileKey)) {
    attachments = [
      {
        type: base.file_type || base.fileType || 'image',
        key: base.file_key || base.fileKey,
        mime: base.mime_type || base.mimeType || null,
        name: base.file_name || base.fileName || null,
        size:
          typeof base.file_size === 'number'
            ? base.file_size
            : base.fileSize || null,
      },
    ];
  }

 const sdRaw =
  base.self_destruct_at ??
  base.selfDestructAt ??
  base.self_destruct_end_time ??
  base.selfDestructEndTime ??
  null;

const sdMs = parseTimeToMs(sdRaw);
const sdAt = sdMs ? new Date(sdMs).toISOString() : null;


  // 🔹 base UI message
  const ui = {
    id: base.message_id || base.id,
    senderType,
    username: isCreator
      ? 'You'
      : (props.member?.profileName || props.member?.name || 'Member'),
    avatar: '/media/images/default-avatar.png',
    time: created.toLocaleTimeString(),
    date: created.toISOString(), // used by your date-divider
    content: base.body || '',
    type: 'text',
    showOptions: false,
    deleting: false,
    showFireTooltip: false,
    attachments: [],
    // ✅ Preserve server timer timestamp for edit-prefill
    selfDestructAt: sdAt,
    selfDestructEndTime: Number.isFinite(sdMs) ? sdMs : null,
  };

  if (attachments.length) {

    const normalized = attachments
  .filter((att) => att && (att.key || att.mediaFileId || att.fileId))

  .map((att) => {

        const mime = att.mime || '';
    const fileId = att.mediaFileId || att.fileId || null;
    const fileName = att.name || att.filename || '';
    const ext = fileName.includes('.') ? fileName.split('.').pop().toLowerCase() : '';

    let attType = att.type || null;

    if (!attType) {
      if (mime.startsWith('image/')) attType = 'image';
      else if (mime.startsWith('video/')) attType = 'video';
      else if (mime.startsWith('audio/')) attType = 'audio';
      else if (mime === 'application/pdf' || ext === 'pdf') attType = 'pdf';
      else if (mime === 'text/plain' || ext === 'txt') attType = 'text';
      else if (
        mime === 'application/msword' ||
        mime ===
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        ext === 'doc' ||
        ext === 'docx'
      ) {
        attType = 'word';
      } else if (
        mime === 'application/vnd.ms-excel' ||
        mime ===
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        ext === 'xls' ||
        ext === 'xlsx'
      ) {
        attType = 'excel';
      } else if (mime === 'text/csv' || ext === 'csv') {
        attType = 'csv';
      } else if (
        mime === 'application/zip' ||
        mime === 'application/x-zip-compressed' ||
        ext === 'zip'
      ) {
        attType = 'zip';
      } else {
        attType = 'image';
      }
    }


    let url = null;
    let posterUrl = null;

    if (attType === 'video') {
      // 🔹 use the SAME proxy family as the player
      if (fileId) {
        // optional: keep this in sync with prepareHlsRequest()
        url = `${apiProxyUrl}/api/chat-videos/master/${fileId}`;
        // ⛔️ DO NOT set posterUrl here; let the helpers use /api/chat-videos/thumbnail
      } else if (att.key) {
        // legacy fallback
        url = `${apiProxyUrl}/api/chat-file?key=${encodeURIComponent(att.key)}`;
      }
    } else {
      if (att.key) {
        url = `${apiProxyUrl}/api/chat-file?key=${encodeURIComponent(att.key)}`;
      }
    }

    // If backend ever sends an explicit *correct* URL, we still allow it:
    if (att.url) url = att.url;
    // 👇 remove this line so we don’t override with /api/stream-hls/thumbnail
    // if (att.posterUrl) posterUrl = att.posterUrl;

    return {
      ...att,
      type: attType,
      url,
      posterUrl,        // will usually be null → computed later
      mediaFileId: fileId,
      name: att.name || att.filename || null,
    };

  });

ui.attachments = normalized;

// Backwards-compatible: if exactly one attachment, also fill old fields
if (normalized.length === 1) {
  const first = normalized[0];

  if (first.type === 'image') {
    ui.type = 'image';
    ui.fileName = first.name || null;
    ui.file = { type: 'image', data: first.url };
  } else if (first.type === 'video') {
    ui.type = 'video';
    ui.fileName = first.name || null;
    // ✅ now this is ALWAYS the HLS playlist URL
    ui.file = { type: 'video', data: first.url };
  } else if (first.type === 'pdf') {
    ui.fileName = first.name || null;
    ui.file = { type: 'pdf', data: first.url };
  } else if (first.type === 'word' || first.type === 'text') {
    ui.fileName = first.name || null;
    ui.file = { type: first.type, url: first.url };
  }
}else {
      // multi-attachment row
      if (!ui.content) {
        ui.type = 'attachments';
      }
    }
  }

  return ui;

};

const upsertIncomingUiMessage = (uiMessage) => {
  if (!uiMessage || !uiMessage.id) return;

  const idx = messages.value.findIndex(
    (m) => String(m.id) === String(uiMessage.id)
  );

  if (idx === -1) {
    // brand new message → append
    messages.value.push(uiMessage);
  } else {
    // same message → merge, but keep local UI flags
    const existing = messages.value[idx];
    messages.value[idx] = {
      ...existing,
      ...uiMessage,
      showOptions: existing.showOptions,
      deleting: existing.deleting,
      showFireTooltip: existing.showFireTooltip,
    };
  }
};


const handleIncomingChatWs = async (payload /*, raw */) => {
  if (!payload) return;

  // meta may be alongside message, or payload itself
  const meta = payload.meta || payload;

  const row =
    payload.message ||
    payload.chatMessage ||
    payload.messageRow ||
    payload;

  if (!props.member || !props.featureInstanceId) return;

  if (
    Number(meta.memberId) !== Number(props.member.id) ||
    Number(meta.featureInstanceId) !== Number(props.featureInstanceId)
  ) {
    // different conversation
    return;
  }

  // ✅ capture BEFORE mutation (and after convo match)
  const wasNearBottom = isUserNearBottom();

  console.log('[MemberChatComponent2] WS chat:newMessage payload:', payload);

  const uiMessage = mapApiMessageToUi(row);
  if (!uiMessage) return;

  upsertIncomingUiMessage(uiMessage);

  // ✅ only stick if user was already near bottom
  if (wasNearBottom) {
    startStickToBottom();
    await forceScrollBottom();
  }

  // 🔥 Any real message for this convo means “stop showing typing”
  clearMemberTyping();

  playIncomingMessageSound();
};


const isUserNearBottom = () => {
  const el = chatContainer.value;
  if (!el) return false;

  const THRESHOLD = 120; // px from bottom
  const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;

  return distanceFromBottom <= THRESHOLD;
};


const handleTypingEvent = async (payload) => {
  if (!payload) return;

  const meta = payload.meta || payload;

  if (!props.member || !props.featureInstanceId) return;

  // Must match current conversation
  if (
    Number(meta.memberId) !== Number(props.member.id) ||
    Number(meta.featureInstanceId) !== Number(props.featureInstanceId)
  ) {
    return;
  }

  // Ignore our own typing events if backend echoes them
  if (meta.senderType === 'creator') return;

  // 🔥 Read the typing flag from WS payload
  const typingFlag =
    payload.isTyping ??
    meta.isTyping ??
    meta.typing ??
    (meta.status === 'typing') ??
    true;

  // If this is an explicit "not typing" → clear bubble and bail
  if (typingFlag === false) {
    clearMemberTyping();
    return;
  }

  const wasTyping = isMemberTyping.value;

  // Turn on the bubble
  isMemberTyping.value = true;

  // Reset timeout so bubble disappears if no new pings
  if (typingTimeoutId.value) {
    clearTimeout(typingTimeoutId.value);
  }

  typingTimeoutId.value = setTimeout(() => {
    isMemberTyping.value = false;
    typingTimeoutId.value = null;
  }, TYPING_TIMEOUT_MS);

  // 👇 bubble just appeared AND user is already near bottom → nudge view
  if (!wasTyping && isUserNearBottom()) {
    await nextTick();
    await scrollToBottom();
  }
};


const handleCreatorUpdatedMessageWs = (payload /*, raw */) => {
  if (!payload) return;

  const meta = payload.meta || payload;

  // Row can come in a few shapes, mirror handleIncomingChatWs
  const row =
    payload.message ||
    payload.chatMessage ||
    payload.messageRow ||
    payload;

  // Optional safety: only process if it matches current convo
  if (props.member && props.featureInstanceId) {
    const mid =
      meta.memberId ??
      row.memberId ??
      row.member_id;

    const fi =
      meta.featureInstanceId ??
      row.featureInstanceId ??
      row.feature_instance_id;

    if (
      Number(mid) !== Number(props.member.id) ||
      Number(fi) !== Number(props.featureInstanceId)
    ) {
      // different conversation
      return;
    }
  }

  const uiMessage = mapApiMessageToUi(row);
  if (!uiMessage) return;

  // merge into existing list
  upsertIncomingUiMessage(uiMessage);
};


let unsubscribeChatMessageDeleted = null;


const clearPendingAttachment = (attachment) => {
  if (!attachment) {
    // fallback: clear everything
    pendingAttachments.value.forEach((att) => {
      if (att.previewUrl) {
        try {
          window.URL.revokeObjectURL(att.previewUrl);
        } catch (e) {
          console.warn('[Chat] Failed to revoke preview URL on clear-all', e);
        }
      }
    });
    pendingAttachments.value = [];
    attachmentsError.value = '';
    if (fileInput.value) fileInput.value.value = '';
    return;
  }

  if (attachment.previewUrl) {
    try {
      window.URL.revokeObjectURL(attachment.previewUrl);
    } catch (e) {
      console.warn('[Chat] Failed to revoke preview URL on clear', e);
    }
  }

  pendingAttachments.value = pendingAttachments.value.filter(
    (att) => att !== attachment
  );

  if (!pendingAttachments.value.length) {
    attachmentsError.value = '';
    if (fileInput.value) fileInput.value.value = '';
  }
};


const formatDate = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return '';

  const month = date.toLocaleString('default', { month: 'long' });
  const day = date.getDate();
  const year = date.getFullYear();

  const ordinal = (d) => {
    if (d > 3 && d < 21) return 'th';
    switch (d % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  return `${month} ${day}${ordinal(day)}, ${year}`;
};


// emoji larger logic
const isOnlyFewEmojis = (text) => {
  if (typeof text !== 'string') {
    console.error('Expected a string but received:', typeof text, text);
    return false;
  }

  const trimmed = text.trim();
  const emojis = Array.from(trimmed).filter((char) => char.match(/\p{Emoji}/u));
  console.log(`Emojis count in "${text}":`, emojis.length);
  
  return emojis.length >= 1 && emojis.length <= 3 && emojis.join('') === trimmed;
};


const fontSizeClass = (text) => {
  return isOnlyFewEmojis(text) ? 'text-4xl' : 'text-base';
};


// Reference to the hidden file input
const fileInput = ref(null);

// Function to trigger the hidden file input when the paperclip icon is clicked

const uploadFileToChat = async (file) => {
  if (!props.member || !props.featureInstanceId) {
    console.warn('[Chat] Missing member or featureInstanceId');
    return null;
  }

  try {
    // 1) Ask creator backend for a signed PUT URL
    const resp = await fetch(`${apiProxyUrl}/api/chat-generate-upload-url`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        featureInstanceId: props.featureInstanceId,
        memberId: props.member.id,
        filename: file.name,
        contentType: file.type,
      }),
    });

    if (!resp.ok) {
      throw new Error(`generate-upload-url failed: ${resp.status}`);
    }

    const { signedUrl, key } = await resp.json();

    // 2) PUT raw file to Exoscale
    const putResp = await fetch(signedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });

    if (!putResp.ok) {
      throw new Error(`PUT upload failed: ${putResp.status}`);
    }

    console.log('[Chat] Uploaded file to key:', key);

    // 3) Infer attachment type from mime
    const mime = file.type || '';
    const name = file.name || '';
    const ext = name.includes('.') ? name.split('.').pop().toLowerCase() : '';

    let type = 'image';

    if (mime === 'application/pdf') {
      type = 'pdf';
      } else if (mime.startsWith('video/')) {
      type = 'video';
      } else if (mime === 'text/plain') {
      type = 'text';
      } else if (mime.startsWith('audio/')) {
      // ✅ NEW: mp3 / audio support
      type = 'audio';
     } else if (
      mime === 'application/msword' ||
      mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      ext === 'doc' ||
      ext === 'docx'
    ) {
      type = 'word';
    } else if (
      mime === 'application/vnd.ms-excel' ||
      mime === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      ext === 'xls' ||
      ext === 'xlsx'
    ) {
      type = 'excel';
    } else if (mime === 'text/csv' || ext === 'csv') {
      type = 'csv';
    } else if (
      mime === 'application/zip' ||
      mime === 'application/x-zip-compressed' ||
      ext === 'zip'
    ) {
      type = 'zip';
    }

    // 4) Return attachment meta
    return {
      type,
      key,
      mime: mime || null,
      name: file.name || null,
      size: typeof file.size === 'number' ? file.size : null,
    };
  } catch (e) {
    console.error('[Chat] Upload failed:', e);
    throw e;
  }
};

const getFileExtension = (fileOrName) => {
  const name =
    typeof fileOrName === 'string'
      ? fileOrName
      : fileOrName?.name || '';
  const idx = name.lastIndexOf('.');
  return idx !== -1 ? name.slice(idx + 1).toLowerCase() : '';
};


const handleFileChange = async (event) => {
  const filesList = event?.target?.files;
  if (!filesList || !filesList.length) return;

  // reset previous error
  attachmentsError.value = '';

  const rawFiles = Array.from(filesList);

  // ─────────────────────────────────────────────
  // 1) SIZE FILTER – enforce MAX_ATTACHMENT_MB
  // ─────────────────────────────────────────────
  const tooLarge = rawFiles.filter(
    (f) => typeof f.size === 'number' && f.size > MAX_ATTACHMENT_BYTES
  );

  if (tooLarge.length) {
    const names = tooLarge.map((f) => f.name).join(', ');
    attachmentsError.value =
      tooLarge.length === 1
        ? `“${names}” is larger than the ${MAX_ATTACHMENT_MB} MB limit and was skipped.`
        : `Some files are larger than the ${MAX_ATTACHMENT_MB} MB limit and were skipped: ${names}`;
  }

  const sizeOkFiles = rawFiles.filter((f) => !tooLarge.includes(f));

  // if everything was too large, bail out
  if (!sizeOkFiles.length) {
    if (fileInput.value) fileInput.value.value = '';
    return;
  }

  // ─────────────────────────────────────────────
  // 2) TYPE FILTER – only allow supported formats
  // ─────────────────────────────────────────────
  const files = sizeOkFiles.filter((f) => {
    if (!f) return false;
    const mime = f.type || '';
    const ext = getFileExtension(f);

    if (
      mime.startsWith('image/') ||
      mime.startsWith('video/') ||
      mime.startsWith('audio/')
    ) {
      return true;
    }

    if (mime === 'application/pdf' || ext === 'pdf') return true;
    if (mime === 'text/plain' || ext === 'txt') return true;

    // Word
    if (
      mime === 'application/msword' ||
      mime ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      ext === 'doc' ||
      ext === 'docx'
    ) {
      return true;
    }

    // Excel
    if (
      mime === 'application/vnd.ms-excel' ||
      mime ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      ext === 'xls' ||
      ext === 'xlsx'
    ) {
      return true;
    }

    // CSV
    if (mime === 'text/csv' || ext === 'csv') return true;

    // ZIP
    if (
      mime === 'application/zip' ||
      mime === 'application/x-zip-compressed' ||
      ext === 'zip'
    ) {
      return true;
    }

    return false;
  });

  if (!files.length) {
    const typeMsg =
      'Only images, videos, PDFs, .txt, .csv, Xcel, Word Doc, and zip files, and audio files (e.g. mp3) can be uploaded to chat.';
    attachmentsError.value = attachmentsError.value
      ? `${attachmentsError.value} ${typeMsg}`
      : typeMsg;

    if (fileInput.value) fileInput.value.value = '';
    return;
  }

  // ─────────────────────────────────────────────
  // 3) GLOBAL ATTACHMENT COUNT LIMIT (max 4)
  // ─────────────────────────────────────────────
  if (pendingAttachments.value.length >= 4) {
    attachmentsError.value =
      'You can only add up to 4 attachments to a chat message.';
    if (fileInput.value) fileInput.value.value = '';
    return;
  }

  // helper to decide attachment type
    const inferType = (file) => {
    if (!file) return 'image';

    const mime = file.type || '';
    const ext = getFileExtension(file);

    if (mime.startsWith('image/')) return 'image';
    if (mime.startsWith('video/')) return 'video';
    if (mime.startsWith('audio/')) return 'audio';

    if (mime === 'application/pdf' || ext === 'pdf') return 'pdf';
    if (mime === 'text/plain' || ext === 'txt') return 'text';

    if (
      mime === 'application/msword' ||
      mime ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      ext === 'doc' ||
      ext === 'docx'
    ) {
      return 'word';
    }

    if (
      mime === 'application/vnd.ms-excel' ||
      mime ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      ext === 'xls' ||
      ext === 'xlsx'
    ) {
      return 'excel';
    }

    if (mime === 'text/csv' || ext === 'csv') return 'csv';

    if (
      mime === 'application/zip' ||
      mime === 'application/x-zip-compressed' ||
      ext === 'zip'
    ) {
      return 'zip';
    }

    return 'image';
  };


  // ─────────────────────────────────────────────
  // 4) EDIT MODE: append to existing attachments
  // ─────────────────────────────────────────────
  if (isEditingMode.value) {
    for (const file of files) {
      if (pendingAttachments.value.length >= 4) {
        attachmentsError.value =
          'You can only add up to 4 attachments to a chat message.';
        break;
      }

      const attType = inferType(file);
      const isVideo = file.type.startsWith('video/');

      const previewUrl = window.URL.createObjectURL(file);

      const attachmentRecord = {
        id: `edit-upload-${Date.now()}-${Math.random()
          .toString(36)
          .slice(2, 8)}`,
        previewUrl,
        type: attType,
        key: null,
        mediaFileId: null,
        mime: file.type || null,
        name: file.name || null,
        size: typeof file.size === 'number' ? file.size : null,
        uploading: true,
      };

      pendingAttachments.value.push(attachmentRecord);

      uploadInProgressCount.value++;
      isUploadingAttachment.value = true;

      await nextTick(); // let preview render

      try {
        let uploadedMeta;

        if (isVideo) {
          // ⬇️ your existing HLS/video pipeline
          uploadedMeta = await uploadChatVideoViaMedia(file);
        } else {
          // ⬇️ your existing generic chat upload (images/pdf/text/audio)
          uploadedMeta = await uploadFileToChat(file);
        }

        if (uploadedMeta) {
          attachmentRecord.key = uploadedMeta.key || null;
          attachmentRecord.type = uploadedMeta.type || attType;
          attachmentRecord.mime = uploadedMeta.mime || attachmentRecord.mime;
          attachmentRecord.mediaFileId =
            uploadedMeta.mediaFileId || uploadedMeta.fileId || null;
          attachmentRecord.size =
            typeof uploadedMeta.size === 'number'
              ? uploadedMeta.size
              : attachmentRecord.size;
          attachmentRecord.name =
            uploadedMeta.name || attachmentRecord.name;
        }
      } catch (e) {
        console.error('[Chat] Upload failed for edit file:', e);
        clearPendingAttachment(attachmentRecord);
      } finally {
        attachmentRecord.uploading = false;
        pendingAttachments.value = [...pendingAttachments.value];

        uploadInProgressCount.value--;
        if (uploadInProgressCount.value <= 0) {
          isUploadingAttachment.value = false;
        }
      }
    }

    if (fileInput.value) {
      fileInput.value.value = '';
    }

    return;
  }

  // ─────────────────────────────────────────────
  // 5) NORMAL (NEW MESSAGE) MODE – multi-file, max 4 total
  // ─────────────────────────────────────────────
  for (const file of files) {
    if (pendingAttachments.value.length >= 4) {
      attachmentsError.value =
        'You can only add up to 4 attachments to a chat message.';
      break;
    }

    const attType = inferType(file);
    const isVideo = file.type.startsWith('video/');

    const previewUrl = window.URL.createObjectURL(file);

    const attachmentRecord = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      previewUrl,
      type: attType,
      key: null,
      mediaFileId: null,
      mime: file.type || null,
      name: file.name || null,
      size: typeof file.size === 'number' ? file.size : null,
      uploading: true,
    };

    pendingAttachments.value.push(attachmentRecord);

    uploadInProgressCount.value++;
    isUploadingAttachment.value = true;

    await nextTick(); // let preview render

    try {
      let uploadedMeta;

      if (isVideo) {
        uploadedMeta = await uploadChatVideoViaMedia(file);
      } else {
        uploadedMeta = await uploadFileToChat(file);
      }

      if (uploadedMeta) {
        attachmentRecord.key = uploadedMeta.key || null;
        attachmentRecord.type = uploadedMeta.type || attType;
        attachmentRecord.mime = uploadedMeta.mime || attachmentRecord.mime;
        attachmentRecord.mediaFileId =
          uploadedMeta.mediaFileId || uploadedMeta.fileId || null;
        attachmentRecord.size =
          typeof uploadedMeta.size === 'number'
            ? uploadedMeta.size
            : attachmentRecord.size;
        attachmentRecord.name =
          uploadedMeta.name || attachmentRecord.name;
      }
    } catch (e) {
      console.error('[Chat] Upload failed for file:', e);
      clearPendingAttachment(attachmentRecord);
    } finally {
      attachmentRecord.uploading = false;
      pendingAttachments.value = [...pendingAttachments.value];

      uploadInProgressCount.value--;
      if (uploadInProgressCount.value <= 0) {
        isUploadingAttachment.value = false;
      }
    }
  }

  if (fileInput.value) {
    fileInput.value.value = '';
  }
};


const hoveredMessageId = ref(null);
const optionsMenu = ref(null);
// 🔽 add this
const optionsHideTimer = ref(null);

function toggleOptions(message) {
  // Close all other open menus
  messages.value.forEach((msg) => {
    if (msg.id !== message.id) {
      msg.showOptions = false;
    }
  });

  // Toggle the current message's options
  message.showOptions = !message.showOptions;
}

const scheduleHideOptions = (message) => {
  if (optionsHideTimer.value) {
    clearTimeout(optionsHideTimer.value);
  }
  optionsHideTimer.value = setTimeout(() => {
    message.showOptions = false;
    optionsHideTimer.value = null;
  }, 150); // small delay so user can move into the menu
};

const cancelHideOptions = () => {
  if (optionsHideTimer.value) {
    clearTimeout(optionsHideTimer.value);
    optionsHideTimer.value = null;
  }
};

const handleEllipsisMouseLeave = (message) => {
  hoveredMessageId.value = null;
  scheduleHideOptions(message);
};

const handleMenuMouseLeave = (message) => {
  cancelHideOptions();
  message.showOptions = false;
  if (hoveredMessageId.value === message.id) {
    hoveredMessageId.value = null;
  }
};

// drag and drop logic
const isDraggingOver = ref(false);
const dragCounter = ref(0); // Counter for drag events

const handleDragEnter = (e) => {
  e.preventDefault();

  if (e.dataTransfer.items && e.dataTransfer.items[0].kind === 'file') {
    dragCounter.value++; // Increment the counter on drag enter

    if (dragCounter.value === 1) {
      // If this is the only drag event currently detected
      isDraggingOver.value = true;
    }
  }
};

const handleDragLeave = (e) => {
  e.preventDefault();

  dragCounter.value--; // Decrement the counter on drag leave

  if (dragCounter.value === 0) {
    // If all drag events are left
    isDraggingOver.value = false;
  }
};

const handleDrop = (e) => {
  e.preventDefault();

  dragCounter.value = 0;
  isDraggingOver.value = false;

  const files = e.dataTransfer?.files;
  if (files && files.length) {
    handleFileChange({ target: { files } });
  }
};

// image / file overlay logic

const isImageOverlayOpen = ref(false); // controls overlay visibility
const currentImageSrc = ref('');       // src for image/pdf/generic file
const overlayType = ref('image');      // 'image' | 'pdf' | 'text' | 'file'
const overlayTextContent = ref('');
const overlayLoading = ref(false);
const overlayError = ref('');
const currentFileName = ref('');       // optional display name
const overlayFileKind = ref('file');   // 'word' | 'excel' | 'csv' | 'zip' | 'file'

const showImageOverlay = (url, type = 'image', name = null) => {
  if (!url) return;

  isImageOverlayOpen.value = true;
  overlayType.value = type; // 'image' | 'pdf'
  currentFileName.value = name || '';

  if (type === 'pdf') {
    // Use the helper so PDFs open nicely with toolbar/nav disabled
    currentImageSrc.value = buildPdfViewerSrc(url);
  } else {
    currentImageSrc.value = url;
  }

  // Reset any text-overlay state
  overlayTextContent.value = '';
  overlayError.value = '';
  overlayLoading.value = false;
  overlayFileKind.value = 'file'; // irrelevant for image/pdf

  // Optional: reset zoom when opening a new image
  zoomLevel.value = 1;
};


const showFileOverlay = (url, name = null, kind = 'file') => {
  if (!url) return;

  isImageOverlayOpen.value = true;
  overlayType.value = 'file';
  currentFileName.value = name || '';
  currentImageSrc.value = url;
  overlayFileKind.value = kind || 'file';

  overlayTextContent.value = '';
  overlayError.value = '';
  overlayLoading.value = false;
};



// image overlay zoom logic
const zoomLevel = ref(1);

// video message logic

const showVideoModal = ref(false);

const toggleVideoModal = () => {
  showVideoModal.value = !showVideoModal.value;
};


// Self-destruct variables
const showSelfDestructMenu = ref(false);
const selfDestructTime = ref(null); // in seconds
// ✅ NEW: only for edit mode — tracks whether user changed timer
const selfDestructTouchedInEdit = ref(false);
const selfDestructHideTimer = ref(null);

const scheduleHideSelfDestructMenu = () => {
  if (selfDestructHideTimer.value) {
    clearTimeout(selfDestructHideTimer.value);
  }
  selfDestructHideTimer.value = setTimeout(() => {
    showSelfDestructMenu.value = false;
    selfDestructHideTimer.value = null;
  }, 150); // small delay so you can move from icon → menu
};

const cancelHideSelfDestructMenu = () => {
  if (selfDestructHideTimer.value) {
    clearTimeout(selfDestructHideTimer.value);
    selfDestructHideTimer.value = null;
  }
};

const toggleSelfDestructMenu = () => {
  // if we’re explicitly clicking, don’t let an old timer fight us
  cancelHideSelfDestructMenu();

  showSelfDestructMenu.value = !showSelfDestructMenu.value;

  // if menu opens, hide tooltip
  if (showSelfDestructMenu.value) {
    showSelfDestructTooltip.value = false;
  }
};

// Set self-destruct time
const setSelfDestructTime = (time) => {
  showSelfDestructMenu.value = false;
  selfDestructTime.value = time;

  // ✅ if editing, we only send selfDestructSeconds when user actually changes it
  if (isEditingMode.value) {
    selfDestructTouchedInEdit.value = true;
  }
};

const handleSelfDestructIconMouseLeave = () => {
  showSelfDestructTooltip.value = false;

  // only schedule hide if menu is currently open
  if (showSelfDestructMenu.value) {
    scheduleHideSelfDestructMenu();
  }
};

const handleSelfDestructMenuMouseLeave = () => {
  // moving out of menu -> actually close it after the tiny delay
  scheduleHideSelfDestructMenu();
};

// Clear self-destruct time
const clearSelfDestructTime = () => {
  showSelfDestructMenu.value = false;
  selfDestructTime.value = null;

  // ✅ mark touched in edit mode so PUT can clear it (send null)
  if (isEditingMode.value) {
    selfDestructTouchedInEdit.value = true;
  }
};


// Function to convert self-destruct seconds to readable time
const secondsToReadableTime = (seconds) => {
  const intervals = [
    { label: 'week', seconds: 7 * 24 * 60 * 60 },
    { label: 'day', seconds: 24 * 60 * 60 },
    { label: 'hour', seconds: 60 * 60 },
    { label: 'minute', seconds: 60 },
    { label: 'second', seconds: 1 }
  ];

  for (const interval of intervals) {
    if (seconds >= interval.seconds) {
      const count = Math.floor(seconds / interval.seconds);
      return `${count} ${interval.label}${count > 1 ? 's' : ''}`;
    }
  }
};

let interval;


const getRemainingSeconds = (endTime) => {
  const now = new Date().getTime();
  const difference = endTime - now;
  return Math.floor(difference / 1000);
};

// voice memo logic

const showVoiceMemoModal = ref(false);

// pending (unsent) voice memos for the input area
const voiceMemos = ref([]);

/**
 * Called when user hits the mic icon.
 * Just opens the modal; recorder component handles everything else.
 */
const showVoiceMemoOverlay = () => {
  showVoiceMemoModal.value = true;
};

/**
 * Close overlay when recorder emits "close"
 */
const closeVoiceMemoOverlay = () => {
  showVoiceMemoModal.value = false;
};

/**
 * Recorder finished successfully and emitted a memo.
 * payload: { url, blob, durationSeconds }
 */
const handleVoiceMemoRecorded = async (payload) => {
  // Expecting { blob } or a raw Blob
  const blob = payload && payload.blob instanceof Blob ? payload.blob
              : payload instanceof Blob ? payload
              : null;

  if (!blob) {
    console.warn('[Chat] handleVoiceMemoRecorded called without a Blob payload');
    return;
  }

  if (!props.member || !props.featureInstanceId) {
    console.warn('[Chat] Cannot attach voice memo – missing member or featureInstanceId');
    return;
  }

  // Wrap Blob in a File so uploadFileToChat works
  const file = new File(
    [blob],
    'Voice memo.webm',
    { type: 'audio/webm' }
  );

  // Local preview URL (for pending attachments area)
  const previewUrl = window.URL.createObjectURL(file);

  const attachmentRecord = {
    id: `voice-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    previewUrl,
    type: 'audio',
    key: null,
    mime: file.type,
    name: file.name,
    size: typeof file.size === 'number' ? file.size : null,
    uploading: true,
    isVoiceMemo: true, // 👈 flag we also check in the template
  };

  pendingAttachments.value.push(attachmentRecord);

  uploadInProgressCount.value++;
  isUploadingAttachment.value = true;

  try {
    // Reuse the same signed-upload flow as other attachments
    const uploadedMeta = await uploadFileToChat(file);
    if (uploadedMeta) {
      attachmentRecord.key = uploadedMeta.key;
      attachmentRecord.type = uploadedMeta.type || 'audio';
      attachmentRecord.mime = uploadedMeta.mime || attachmentRecord.mime;
    }
  } catch (e) {
    console.error('[Chat] Voice memo upload failed:', e);
    clearPendingAttachment(attachmentRecord);
    } finally {
    attachmentRecord.uploading = false;
    pendingAttachments.value = [...pendingAttachments.value];

    uploadInProgressCount.value--;
    if (uploadInProgressCount.value <= 0) {
      isUploadingAttachment.value = false;
    }
  }

  // Close the voice memo modal after recording finishes
  showVoiceMemoModal.value = false;
};


/**
 * Delete memo from input area when child emits "delete"
 */
const deleteAudio = (memoToDelete) => {
  if (!memoToDelete) return;
  voiceMemos.value = voiceMemos.value.filter(
    (m) =>
      m !== memoToDelete &&
      m.id !== memoToDelete.id &&
      m.url !== memoToDelete.url
  );
};


const handleSelfDestruct = (message) => {
  if (selfDestructTime.value) {
    // Calculate the self-destruct time (in milliseconds)
    const endTime = new Date().getTime() + selfDestructTime.value * 1000;

    // Attach the self-destruct end time to the message
    message.selfDestructEndTime = endTime;

    // Schedule the deletion of the message
    setTimeout(() => {
      deleteMessage(message);
    }, selfDestructTime.value * 1000);
  }
};

const uploadChatVideoViaMedia = async (file) => {
  if (!props.member || !props.featureInstanceId) {
    console.warn('[Chat] Missing member or featureInstanceId for video upload', {
      member: props.member,
      featureInstanceId: props.featureInstanceId,
    });
    return null;
  }

  const fileType = 'video';

  console.log('[uploadChatVideoViaMedia] starting', {
    name: file.name,
    type: file.type,
    size: file.size,
  });

  // 1️⃣ Ask creator BFF for media signed URL
  //    BFF route: POST /api/generate-signed-url → backend /api/media/generate-signed-url
  const signedRes = await fetch(`${apiProxyUrl}/api/generate-signed-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      filename: file.name,
      contentType: file.type || 'application/octet-stream',
      fileType,       // 'video' → media pipeline
      folderId: null, // chat videos are not in a drive folder
    }),
  });

  if (!signedRes.ok) {
    const text = await signedRes.text().catch(() => '');
    console.error(
      '[uploadChatVideoViaMedia] generate-signed-url failed:',
      signedRes.status,
      text
    );
    throw new Error('Failed to get signed URL for video');
  }

  const signedJson = await signedRes.json();
  const { fileId, signedUrl, permanentUrl } = signedJson;

  console.log('[uploadChatVideoViaMedia] signed URL OK:', {
    fileId,
    hasSignedUrl: !!signedUrl,
    permanentUrl,
  });

  // 2️⃣ PUT the raw file to storage
  const putRes = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });

  if (!putRes.ok) {
    const text = await putRes.text().catch(() => '');
    console.error(
      '[uploadChatVideoViaMedia] PUT to signedUrl failed:',
      putRes.status,
      text
    );
    throw new Error('Failed to upload video to storage');
  }

  console.log('[uploadChatVideoViaMedia] upload to storage OK for', fileId);

  // 3️⃣ Create media_files record + enqueue thumbnail + HLS
  //    BFF route: POST /api/create-record → backend /api/media/create-record
  const recordRes = await fetch(`${apiProxyUrl}/api/create-record`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      fileId,
      fileName: file.name,
      fileUrl: permanentUrl,
      fileType,                               // 'video'
      mimeType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
      folderId: null,
      durationSeconds: null,
      thumbnailUrl: null,
    }),
  });

  if (!recordRes.ok) {
    const text = await recordRes.text().catch(() => '');
    console.error(
      '[uploadChatVideoViaMedia] create-record failed:',
      recordRes.status,
      text
    );
    throw new Error('Failed to create media record for video');
  }

  console.log('[uploadChatVideoViaMedia] create-record OK for', fileId);

  // 4️⃣ Return metadata to be stored in pendingAttachments + sent in attachment_json
  const meta = {
    type: 'video',
    mediaFileId: fileId,                      // 👈 THIS is what chat cares about
    key: null,                                // chat does NOT need a key for video
    mime: file.type || null,
    name: file.name || null,
    size: typeof file.size === 'number' ? file.size : null,
  };

  console.log('[uploadChatVideoViaMedia] returning meta:', meta);
  return meta;
};



// Auto-scroll when number of messages changes (new msg, send, ws)
watch(
  () => messages.value.length,
  async () => {
    // 🔹 Skip the next auto-scroll when we explicitly say so
    if (suppressNextScroll.value) {
      suppressNextScroll.value = false;
      return;
    }

    // 👇 Only auto-scroll if we're in a "stick to bottom" window
    // (i.e., user was at/near bottom recently and hasn't scrolled away)
    if (!shouldStickToBottom()) {
      return;
    }

    await scrollToBottom();
  }
);


// 🔹 NEW: when attachments are added/removed, force the same layout refresh
watch(
  () => pendingAttachments.value.length,
  async () => {
    // give Vue a tick to render the attachment preview block
    await nextTick();
    await resizeTextarea();
  }
);


// 🔄 Keep global "isUploadingAttachment" in sync with actual attachment.uploading flags
watch(
  () => pendingAttachments.value.map(att => att.uploading),
  (uploadFlags) => {
    const anyUploading = uploadFlags.some(Boolean);
    if (isUploadingAttachment.value !== anyUploading) {
      isUploadingAttachment.value = anyUploading;
    }
  },
  { deep: true }
);


watch(
  () => isMemberTyping.value,
  (val) => {
    emit('member-typing', val);
  }
);

watch(
  () => [props.member, props.featureInstanceId],
  (newVals, oldVals) => {
    const [newMember, newFeatureInstanceId] = newVals || [];
    const [oldMember, oldFeatureInstanceId] = oldVals || [];

    const conversationChanged =
      !oldMember ||
      !oldFeatureInstanceId ||
      !newMember ||
      !newFeatureInstanceId ||
      Number(newMember.id) !== Number(oldMember.id) ||
      Number(newFeatureInstanceId) !== Number(oldFeatureInstanceId);

    // 🔥 Any time we switch threads, nuke the typing bubble + timeout
    if (conversationChanged) {
      clearMemberTyping();
    }

    if (!newMember || !newFeatureInstanceId) {
      activeMemberId.value = null;
      messages.value = [];
      return;
    }

    activeMemberId.value = newMember.id;
    loadConversation(newMember.id, newFeatureInstanceId);
  },
  { immediate: true }
);

onMounted(() => {
  // make sure WS is connected
  websocketStore.connect();

  // init audio once
  initIncomingMessageAudio();

  // 🔔 new messages from member
  unsubscribeChatNewMessage = websocketStore.subscribe(
    'chat:newMessage',
    (payload, raw) => {
      handleIncomingChatWs(payload, raw);
    }
  );

  // ❌ deletions from member
  unsubscribeChatMessageDeleted = websocketStore.subscribe(
    'chat:messageDeleted',
    (payload) => {
      if (!payload || !payload.deletedMessageId) return;

      // If this component only shows ONE conversation,
      // we can just remove by id without checking featureInstanceId.
      const idx = messages.value.findIndex(
        (m) => String(m.id) === String(payload.deletedMessageId)
      );
      if (idx !== -1) {
        messages.value.splice(idx, 1);
      }
    }
  );

  // 👇 Typing events from backend
  unsubscribeChatTyping = websocketStore.subscribe(
    'chat:typing',
    (payload) => {
      handleTypingEvent(payload);
    }
  );

  // 👇 Member edited a message → update in creator chat
  unsubscribeChatUpdated = websocketStore.subscribe(
    'creator_chat_message_updated',
    (payload, raw) => {
      handleCreatorUpdatedMessageWs(payload, raw);
    }
  );

});


onUnmounted(() => {

  clearInterval(interval);

  if (unsubscribeChatNewMessage) {
    unsubscribeChatNewMessage();
    unsubscribeChatNewMessage = null;
  }
  if (unsubscribeChatMessageDeleted) {
    unsubscribeChatMessageDeleted();
    unsubscribeChatMessageDeleted = null;
  }
  // 👇 NEW
  if (unsubscribeChatTyping) {
    unsubscribeChatTyping();
    unsubscribeChatTyping = null;
  }

  if (unsubscribeChatUpdated) {
    unsubscribeChatUpdated();
    unsubscribeChatUpdated = null;
  }

  // 🔹 Options (ellipsis) menu timer
  if (optionsHideTimer.value) {
    clearTimeout(optionsHideTimer.value);
    optionsHideTimer.value = null;
  }

  // 🔹 Self-destruct menu timer
  if (selfDestructHideTimer.value) {
    clearTimeout(selfDestructHideTimer.value);
    selfDestructHideTimer.value = null;
  }
});

</script>


<style scoped>


.emoji {
  font-family: 'Noto Color Emoji', sans-serif;
}

.message-fade-leave-active,
.message-fade-move {
  transition: all 0.9s ease;
}

.message-fade-leave-to /* .message-fade-leave-active in <2.1.8 */ {
  opacity: 0;
  transform: scale(0);
}

/* progress bar styles */
/* Keyframe animation for gradient movement */
@keyframes progressAnimation {
  0% {
    background-position: 200% 0%;
  }
  100% {
    background-position: -200% 0%;
  }
}

.progress-bar {
  position: relative;
}

.progress {
  position: absolute;
  top: 0;
  left: 0;
  width: 0; /* We will update this value with JS based on progress */
  height: 100%;
  background-image: linear-gradient(-45deg, #f06, #9f6, #f06);
  background-size: 200% 200%;
  animation: progressAnimation 2s linear infinite;
}

/* This is for when the image overlay is opening */
/* image overlay transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter-from, .fade-enter, .fade-leave-to /* .fade-leave-active in <2.1.8 */ {
  opacity: 0;
}
.fade-enter-to {
  opacity: 1;
}

/* ✅ Hover-only, nearly invisible scrollbar */
:deep(.chat-scroll) {
  scrollbar-width: thin;                 /* Firefox */
  scrollbar-color: transparent transparent;
}

/* WebKit base */
:deep(.chat-scroll::-webkit-scrollbar) {
  width: 6px;
  height: 6px;
}

/* ❌ Kill classic buttons (no-op for overlay, harmless otherwise) */
:deep(.chat-scroll::-webkit-scrollbar-button) {
  display: none;
  width: 0;
  height: 0;
  background: transparent;
  background-color: transparent;
}

/* Track fully invisible */
:deep(.chat-scroll::-webkit-scrollbar-track) {
  background: transparent;
}

/* Thumb invisible by default */
:deep(.chat-scroll::-webkit-scrollbar-thumb) {
  background-color: transparent;
  border-radius: 999px;
}

/* ✅ On hover: fade scrollbar in */
:deep(.chat-scroll:hover) {
  scrollbar-color: rgba(203, 213, 225, 0.45) transparent; /* Firefox */
}

:deep(.chat-scroll:hover::-webkit-scrollbar-thumb) {
  background-color: rgba(203, 213, 225, 0.45);
}

/* Slightly stronger on thumb hover */
:deep(.chat-scroll:hover::-webkit-scrollbar-thumb:hover) {
  background-color: rgba(203, 213, 225, 0.65);
}

/* Prevent janky reflow when older messages are prepended */
.message-fade-move {
  transition: none !important;
}





.typing-dot {
  animation: typing-pulse 1.2s infinite ease-in-out;
}

/* staggered delays for the 3 dots */
.typing-dot-2 {
  animation-delay: 0.15s;
}
.typing-dot-3 {
  animation-delay: 0.3s;
}

@keyframes typing-pulse {
  0%, 100% {
    opacity: 0.4;
  }
  50% {
    opacity: 1;
  }
}


</style>
